import React from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNotifications } from '@/context/NotificationContext';
import { db, collection, onSnapshot, updateDoc, doc, query, orderBy, getDoc, setDoc, addDoc, deleteDoc, storage, ref, uploadBytes, getDownloadURL } from '@/firebase';
import { generateReceipt } from '@/utils/pdfReceipt';
import ReceiptModal from '@/components/ReceiptModal';
import { formatDateSafe } from '@/utils/formatters';
import { updateFaviconLinks } from '@/components/DynamicFavicon';
import { 
  LayoutDashboard, 
  ShoppingBag, 
  Briefcase,
  Users, 
  CheckCircle2, 
  XCircle, 
  Clock,
  Eye,
  MoreVertical,
  Phone,
  MessageCircle,
  Settings,
  Save,
  Wallet,
  Info,
  Package,
  Plus,
  Trash2,
  Edit,
  Layout,
  Newspaper,
  FileText,
  Star,
  Search,
  Printer,
  HelpCircle,
  Image as ImageIcon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import ArticleContentRenderer from '@/components/ArticleContentRenderer';
import ArticleEditorToolbar from '@/components/ArticleEditorToolbar';
import ProductDescriptionRenderer from '@/components/ProductDescriptionRenderer';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface Order {
  id: string;
  productName?: string;
  userEmail?: string;
  userName?: string;
  userPhone?: string;
  amount?: number;
  status: string;
  createdAt?: any;
  proofUrl?: string;
  fileUrl?: string;
  fileUploadedAt?: any;
  rating?: number;
  reviewText?: string;
  ratedAt?: any;
}

interface Product {
  id: string;
  name: string;
  category: string;
  type: string;
  price: number;
  desc: string;
  image: string;
  previewImage?: string;
  appLink?: string;
  createdAt?: any;
}

interface Article {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  imageUrl: string;
  category: string;
  status: 'draft' | 'published';
  relatedProductId?: string;
  createdAt?: any;
}

interface Regulation {
  id: string;
  title: string;
  description: string;
  year: number;
  category: string;
  fileUrl: string;
  downloadCount: number;
  createdAt?: any;
}

interface AdminUser {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
  phoneNumber?: string;
  companyName?: string;
  address?: string;
  role: 'admin' | 'user';
  createdAt?: any;
}

export default function AdminPanel() {
  const { isAdmin, user, loading } = useAuth();
  const { addNotification } = useNotifications();
  const isAuthorized = isAdmin || user?.email === 'azmankoe916@gmail.com' || user?.email === 'azmancivd@gmail.com';
  const [orders, setOrders] = React.useState<Order[]>([]);
  const [selectedReceiptOrder, setSelectedReceiptOrder] = React.useState<any | null>(null);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = React.useState(false);
  const [products, setProducts] = React.useState<Product[]>([]);
  const [articles, setArticles] = React.useState<Article[]>([]);
  const [regulations, setRegulations] = React.useState<Regulation[]>([]);
  const [users, setUsers] = React.useState<AdminUser[]>([]);
  const [productReviews, setProductReviews] = React.useState<any[]>([]);
  const [adminFaqs, setAdminFaqs] = React.useState<any[]>([]);
  const [isAddFaqOpen, setIsAddFaqOpen] = React.useState(false);
  const [editingFaq, setEditingFaq] = React.useState<any | null>(null);
  const [newFaq, setNewFaq] = React.useState({
    question: '',
    answer: '',
    category: 'Format Dokumen',
    order: 1
  });
  const [isAddReviewOpen, setIsAddReviewOpen] = React.useState(false);
  const [newReview, setNewReview] = React.useState({
    userName: '',
    rating: 5,
    reviewText: '',
    productName: '',
    userEmail: ''
  });
  const [paymentSettings, setPaymentSettings] = React.useState({
    danaBusinessId: '',
    merchantName: 'AZMAN 204'
  });
  const [promoProductSettings, setPromoProductSettings] = React.useState({
    isActive: true,
    title: 'Produk Digital Unggulan',
    subtitle: 'Dapatkan koleksi dokumen dan aplikasi terbaik khusus bulan ini.',
    bannerUrl: 'https://images.unsplash.com/photo-1541888086225-f673f41cde00?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    badgeText: 'Promo Produk'
  });
  const [promoServiceSettings, setPromoServiceSettings] = React.useState({
    isActive: true,
    title: 'Layanan Administrasi Profesional',
    subtitle: 'Solusi legalitas dan pendampingan bisnis tercepat dan terpercaya.',
    bannerUrl: 'https://images.unsplash.com/photo-1454165833767-027ffea10c3b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    badgeText: 'Promo Layanan'
  });
  
  const [homeSliderSettings, setHomeSliderSettings] = React.useState({
    isActive: true,
    banners: [
      { url: 'https://images.unsplash.com/photo-1541888086225-f673f41cde00?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80', active: true },
      { url: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80', active: true },
      { url: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80', active: true }
    ]
  });
  const [websiteLogoUrl, setWebsiteLogoUrl] = React.useState('');

  const [isSavingSettings, setIsSavingSettings] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState('orders');
  const [userSearchText, setUserSearchText] = React.useState('');
  const [stats, setStats] = React.useState({
    totalOrders: 0,
    pendingOrders: 0,
    totalRevenue: 0,
    totalProducts: 0
  });

  const [isAddProductOpen, setIsAddProductOpen] = React.useState(false);
  const [isAddArticleOpen, setIsAddArticleOpen] = React.useState(false);
  const [isAddRegulationOpen, setIsAddRegulationOpen] = React.useState(false);
  const [editingProduct, setEditingProduct] = React.useState<Product | null>(null);
  const [editingArticle, setEditingArticle] = React.useState<Article | null>(null);
  const [editingRegulation, setEditingRegulation] = React.useState<Regulation | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [newProduct, setNewProduct] = React.useState({
    name: '',
    category: 'document',
    type: '',
    price: '',
    desc: '',
    image: 'https://picsum.photos/seed/product/400/300',
    previewImage: '',
    appLink: ''
  });
  const [newArticle, setNewArticle] = React.useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    imageUrl: 'https://picsum.photos/seed/article/800/400',
    category: 'Konstruksi',
    status: 'published' as 'draft' | 'published',
    relatedProductId: ''
  });
  const articleTextareaRef = React.useRef<HTMLTextAreaElement>(null);
  const [newRegulation, setNewRegulation] = React.useState({
    title: '',
    description: '',
    year: new Date().getFullYear(),
    category: 'Peraturan Pemerintah',
    fileUrl: ''
  });

  React.useEffect(() => {
    if (loading || !isAuthorized) return;

    const q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ordersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Order));
      setOrders(ordersData);

      // Calculate stats
      const total = ordersData.length;
      const pending = ordersData.filter(o => o.status === 'pending' || o.status === 'processing').length;
      const revenue = ordersData.filter(o => o.status === 'completed').reduce((acc, o) => acc + (o.amount || 0), 0);
      
      setStats(prev => ({ ...prev, totalOrders: total, pendingOrders: pending, totalRevenue: revenue }));
    });

    const qProducts = query(collection(db, 'products'), orderBy('createdAt', 'desc'));
    const unsubscribeProducts = onSnapshot(qProducts, (snapshot) => {
      const productsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Product));
      setProducts(productsData);
      setStats(prev => ({ ...prev, totalProducts: productsData.length }));
    });

    const qArticles = query(collection(db, 'articles'), orderBy('createdAt', 'desc'));
    const unsubscribeArticles = onSnapshot(qArticles, (snapshot) => {
      const articlesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Article));
      setArticles(articlesData);
    });

    const qRegs = query(collection(db, 'regulations'), orderBy('year', 'desc'));
    const unsubscribeRegs = onSnapshot(qRegs, (snapshot) => {
      const regsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Regulation));
      setRegulations(regsData);
    });

    const qUsers = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
    const unsubscribeUsers = onSnapshot(qUsers, (snapshot) => {
      const usersData = snapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() } as AdminUser));
      setUsers(usersData);
    });

    const qReviews = query(collection(db, 'productReviews'), orderBy('createdAt', 'desc'));
    const unsubscribeReviews = onSnapshot(qReviews, (snapshot) => {
      const reviewsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProductReviews(reviewsData);
    });

    const qFaqs = collection(db, 'faqs');
    const unsubscribeFaqs = onSnapshot(qFaqs, (snapshot) => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      items.sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
      setAdminFaqs(items);
    });

    return () => {
      unsubscribe();
      unsubscribeProducts();
      unsubscribeArticles();
      unsubscribeRegs();
      unsubscribeUsers();
      unsubscribeReviews();
      unsubscribeFaqs();
    }
  }, [isAuthorized, loading]);

  React.useEffect(() => {
    if (loading || !isAuthorized) return;

    const fetchSettings = async () => {
      try {
        const settingsSnap = await getDoc(doc(db, 'settings', 'payment'));
        if (settingsSnap.exists()) {
          setPaymentSettings(settingsSnap.data() as any);
        }
        const promoProductSnap = await getDoc(doc(db, 'settings', 'promo_products'));
        if (promoProductSnap.exists()) {
          setPromoProductSettings(promoProductSnap.data() as any);
        } else {
          // Backward compatibility: try to fetch from old 'promo' doc if it exists
          const oldPromoSnap = await getDoc(doc(db, 'settings', 'promo'));
          if (oldPromoSnap.exists()) {
            setPromoProductSettings(oldPromoSnap.data() as any);
          }
        }

        const promoServiceSnap = await getDoc(doc(db, 'settings', 'promo_services'));
        if (promoServiceSnap.exists()) {
          setPromoServiceSettings(promoServiceSnap.data() as any);
        }

        const homeSliderSnap = await getDoc(doc(db, 'settings', 'home_slider'));
        if (homeSliderSnap.exists()) {
          setHomeSliderSettings(homeSliderSnap.data() as any);
        }

        const websiteSnap = await getDoc(doc(db, 'settings', 'website'));
        if (websiteSnap.exists() && websiteSnap.data()?.logoUrl) {
          setWebsiteLogoUrl(websiteSnap.data().logoUrl);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };

    fetchSettings();
  }, [isAuthorized, loading]);

  const handleSavePaymentSettings = async () => {
    setIsSavingSettings(true);
    try {
      await setDoc(doc(db, 'settings', 'payment'), {
        ...paymentSettings,
        updatedAt: new Date()
      });
      toast.success("Pengaturan pembayaran berhasil disimpan!");
    } catch (error) {
      console.error('Error saving payment settings:', error);
      toast.error("Gagal menyimpan pengaturan pembayaran.");
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleSavePromoProductSettings = async () => {
    setIsSavingSettings(true);
    try {
      await setDoc(doc(db, 'settings', 'promo_products'), {
        ...promoProductSettings,
        updatedAt: new Date()
      });
      toast.success("Promo produk berhasil disimpan!");
    } catch (error) {
      console.error('Error saving promo product settings:', error);
      toast.error("Gagal menyimpan promo produk.");
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleSavePromoServiceSettings = async () => {
    setIsSavingSettings(true);
    try {
      await setDoc(doc(db, 'settings', 'promo_services'), {
        ...promoServiceSettings,
        updatedAt: new Date()
      });
      toast.success("Promo layanan berhasil disimpan!");
    } catch (error) {
      console.error('Error saving promo service settings:', error);
      toast.error("Gagal menyimpan promo layanan.");
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleSaveWebsiteLogo = async () => {
    setIsSavingSettings(true);
    try {
      const trimmedLogo = websiteLogoUrl.trim();
      await setDoc(doc(db, 'settings', 'website'), {
        logoUrl: trimmedLogo,
        updatedAt: new Date()
      }, { merge: true });
      if (trimmedLogo) {
        updateFaviconLinks(trimmedLogo);
      } else {
        updateFaviconLinks('/favicon.png');
      }
      toast.success("Logo website & favicon berhasil diperbarui!");
    } catch (error) {
      console.error('Error saving website logo:', error);
      toast.error("Gagal menyimpan logo website.");
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleDeleteReview = async (reviewId: string) => {
    if (window.confirm("Apakah Anda yakin ingin menghapus ulasan ini?")) {
      try {
        await deleteDoc(doc(db, 'productReviews', reviewId));
        toast.success("Ulasan berhasil dihapus!");
      } catch (error) {
        console.error("Gagal menghapus ulasan:", error);
        toast.error("Gagal menghapus ulasan");
      }
    }
  };

  const handleAddReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newReview.userName || !newReview.reviewText || !newReview.productName) {
      toast.error("Semua field bertanda bintang wajib diisi!");
      return;
    }
    setIsSubmitting(true);
    try {
      await addDoc(collection(db, 'productReviews'), {
        userName: newReview.userName,
        userEmail: newReview.userEmail || 'klien@azman204.com',
        rating: Number(newReview.rating),
        reviewText: newReview.reviewText,
        productName: newReview.productName,
        createdAt: new Date(),
        productId: 'template_custom'
      });
      toast.success("Ulasan berhasil ditambahkan!");
      setIsAddReviewOpen(false);
      setNewReview({
        userName: '',
        rating: 5,
        reviewText: '',
        productName: '',
        userEmail: ''
      });
    } catch (error) {
      console.error("Gagal menambahkan ulasan:", error);
      toast.error("Gagal menambahkan ulasan");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveHomeSliderSettings = async () => {
    setIsSavingSettings(true);
    try {
      await setDoc(doc(db, 'settings', 'home_slider'), {
        ...homeSliderSettings,
        updatedAt: new Date()
      });
      toast.success("Slider Beranda berhasil disimpan!");
    } catch (error) {
      console.error('Error saving home slider settings:', error);
      toast.error("Gagal menyimpan slider beranda.");
    } finally {
      setIsSavingSettings(false);
    }
  };

  const formatWhatsAppNumber = (phoneStr: string) => {
    if (!phoneStr) return '6281261089400';
    let clean = phoneStr.replace(/\D/g, '');
    if (clean === '82272041885') return '6281261089400'; // Hardcode fallback for the user's specific test number
    if (clean.startsWith('08')) {
      return '62' + clean.substring(1);
    }
    if (clean.startsWith('8')) {
      return '62' + clean;
    }
    return clean;
  };

  const sendWhatsAppConfirmation = (order: Order) => {
    if (!order.userPhone) {
      toast.error("Nomor WhatsApp pembeli tidak tersedia.");
      return;
    }
    const message = `Halo ${order.userName || 'Pelanggan'},\n\nPembayaran Anda untuk pesanan *${order.productName}* sebesar *Rp ${order.amount?.toLocaleString('id-ID')}* telah kami terima.\n\nFile/Layanan Anda sekarang sudah dapat diakses melalui Dashboard Azman204. Terima kasih telah mempercayai layanan kami! 🙏`;
    const encodedMessage = encodeURIComponent(message);
    const waNumber = formatWhatsAppNumber(order.userPhone);
    window.open(`https://api.whatsapp.com/send?phone=${waNumber}&text=${encodedMessage}`, '_blank');
  };

  const updateOrderFields = async (orderId: string, fields: any) => {
    try {
      // Filter out undefined values to prevent "Unsupported field value: undefined" errors
      const sanitizedFields = Object.fromEntries(
        Object.entries(fields).filter(([_, v]) => v !== undefined)
      );
      await updateDoc(doc(db, 'orders', orderId), { ...sanitizedFields, updatedAt: new Date() });
      if (fields.status) {
        let title = "Status Pesanan";
        let message = `Pesanan Anda ada pembaruan status.`;
        if (fields.status === 'processing') message = `Pesanan Anda sedang diproses.`;
        if (fields.status === 'completed') message = `Pesanan Anda telah selesai.`;
        if (fields.status === 'cancelled') message = `Pesanan Anda dibatalkan.`;
        
        const targetOrder = orders.find(o => o.id === orderId);
        if (targetOrder) {
          addNotification(title, message, 'order', (targetOrder as any).userId);
        }
      }
      toast.success("Pesanan diperbarui!");
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error("Gagal memperbarui pesanan.");
    }
  };

  const updateOrderStatus = async (orderId: string, status: string) => {
    updateOrderFields(orderId, { status });
  };

  const [deleteDialog, setDeleteDialog] = React.useState<{isOpen: boolean, type: 'order' | 'product', id: string}>({ isOpen: false, type: 'order', id: '' });
  const [editingFileUrlOrder, setEditingFileUrlOrder] = React.useState<Order | null>(null);
  const [isEditFileUrlOpen, setIsEditFileUrlOpen] = React.useState(false);
  const [isUploading, setIsUploading] = React.useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !editingFileUrlOrder) return;
    
    setIsUploading(true);
    toast.loading("Sedang mengunggah file...");
    
    try {
      const storageRef = ref(storage, `orders/${editingFileUrlOrder.id}/${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      setEditingFileUrlOrder(prev => prev ? {...prev, fileUrl: downloadURL} : null);
      await updateOrderFields(editingFileUrlOrder.id, { fileUrl: downloadURL, fileUploadedAt: new Date() });
      toast.dismiss();
      toast.success("File berhasil diunggah!");
    } catch (err) {
      console.error(err);
      toast.dismiss();
      toast.error("Gagal mengunggah file. Pastikan Anda memiliki akses.");
    } finally {
      setIsUploading(false);
    }
  };

  const confirmDeleteOrder = async () => {
    if (!deleteDialog.id) return;
    try {
      await deleteDoc(doc(db, 'orders', deleteDialog.id));
      toast.success("Pesanan berhasil dihapus!");
    } catch (error: any) {
      console.error('Error deleting order:', error);
      toast.error(`Gagal menghapus pesanan: ${error?.message || "Unknown error"}`);
    } finally {
      setDeleteDialog({ isOpen: false, type: 'order', id: '' });
    }
  };

  const deleteOrder = (orderId: string) => {
    setDeleteDialog({ isOpen: true, type: 'order', id: orderId });
  };

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProduct.name || !newProduct.price) {
      toast.error("Nama dan harga produk wajib diisi.");
      return;
    }
    
    setIsSubmitting(true);
    try {
      if (editingProduct) {
        await updateDoc(doc(db, 'products', editingProduct.id), {
          name: newProduct.name,
          category: newProduct.category,
          type: newProduct.type || 'Pilihan',
          price: Number(newProduct.price),
          desc: newProduct.desc,
          image: newProduct.image || 'https://picsum.photos/seed/product/400/300',
          previewImage: newProduct.previewImage || '',
          appLink: newProduct.appLink || '',
          updatedAt: new Date()
        });
        toast.success("Produk berhasil diperbarui!");
      } else {
        await addDoc(collection(db, 'products'), {
          name: newProduct.name,
          category: newProduct.category,
          type: newProduct.type || 'Pilihan',
          price: Number(newProduct.price),
          desc: newProduct.desc,
          image: newProduct.image || 'https://picsum.photos/seed/product/400/300',
          previewImage: newProduct.previewImage || '',
          appLink: newProduct.appLink || '',
          createdAt: new Date(),
          updatedAt: new Date()
        });
        addNotification("Produk Baru Rilis!", `Produk unggulan "${newProduct.name}" telah ditambahkan. Segera cek panel produk!`, 'product', 'all');
        toast.success("Produk berhasil ditambahkan!");
      }
      setIsAddProductOpen(false);
      setEditingProduct(null);
      setNewProduct({
        name: '',
        category: 'document',
        type: '',
        price: '',
        desc: '',
        image: `https://picsum.photos/seed/${Math.random().toString().slice(2,8)}/400/300`,
        previewImage: '',
        appLink: ''
      });
    } catch (error) {
      console.error('Error adding/updating product:', error);
      toast.error("Gagal menyimpan produk.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setNewProduct({
      name: product.name,
      category: product.category,
      type: product.type,
      price: product.price.toString(),
      desc: product.desc,
      image: product.image,
      previewImage: product.previewImage || '',
      appLink: product.appLink || ''
    });
    setIsAddProductOpen(true);
  };

  const confirmDeleteProduct = async () => {
    if (!deleteDialog.id) return;
    try {
      await deleteDoc(doc(db, 'products', deleteDialog.id));
      toast.success("Produk berhasil dihapus!");
    } catch (error: any) {
      console.error('Error deleting product:', error);
      toast.error(`Gagal menghapus produk: ${error?.message || "Unknown error"}`);
    } finally {
      setDeleteDialog({ isOpen: false, type: 'product', id: '' });
    }
  };

  const handleDeleteProduct = (productId: string) => {
    setDeleteDialog({ isOpen: true, type: 'product', id: productId });
  };

  const handleAddArticle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newArticle.title || !newArticle.content) {
      toast.error("Judul dan isi artikel wajib diisi.");
      return;
    }
    
    setIsSubmitting(true);
    try {
      const slug = newArticle.slug || newArticle.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      
      if (editingArticle) {
        await updateDoc(doc(db, 'articles', editingArticle.id), {
          ...newArticle,
          slug,
          updatedAt: new Date()
        });
        toast.success("Artikel berhasil diperbarui!");
      } else {
        await addDoc(collection(db, 'articles'), {
          ...newArticle,
          slug,
          authorId: user?.uid,
          authorName: user?.displayName || 'Admin',
          createdAt: new Date(),
          updatedAt: new Date()
        });
        addNotification("Artikel Baru 📢", `"${newArticle.title}" baru saja dipublikasikan. Jangan lewatkan ilmu terbarunya!`, 'article', 'all');
        toast.success("Artikel berhasil diterbitkan!");
      }
      setIsAddArticleOpen(false);
      setEditingArticle(null);
      setNewArticle({
        title: '',
        slug: '',
        content: '',
        excerpt: '',
        imageUrl: `https://picsum.photos/seed/${Math.random().toString().slice(2,8)}/800/400`,
        category: 'Konstruksi',
        status: 'published',
        relatedProductId: ''
      });
    } catch (error) {
      console.error('Error adding/updating article:', error);
      toast.error("Gagal menyimpan artikel.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditArticle = (article: Article) => {
    setEditingArticle(article);
    setNewArticle({
      title: article.title,
      slug: article.slug,
      content: article.content,
      excerpt: article.excerpt,
      imageUrl: article.imageUrl,
      category: article.category,
      status: article.status,
      relatedProductId: article.relatedProductId || ''
    });
    setIsAddArticleOpen(true);
  };

  const [deleteArticleDialog, setDeleteArticleDialog] = React.useState({ isOpen: false, id: '' });

  const confirmDeleteArticle = async () => {
    if (!deleteArticleDialog.id) return;
    try {
      await deleteDoc(doc(db, 'articles', deleteArticleDialog.id));
      toast.success("Artikel berhasil dihapus!");
    } catch (error: any) {
      console.error('Error deleting article:', error);
      toast.error(`Gagal menghapus artikel.`);
    } finally {
      setDeleteArticleDialog({ isOpen: false, id: '' });
    }
  };

  const handleAddRegulation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRegulation.title || !newRegulation.fileUrl) {
      toast.error("Judul dan URL file wajib diisi.");
      return;
    }
    
    setIsSubmitting(true);
    try {
      if (editingRegulation) {
        await updateDoc(doc(db, 'regulations', editingRegulation.id), {
          ...newRegulation,
          year: Number(newRegulation.year),
          updatedAt: new Date()
        });
        toast.success("Regulasi berhasil diperbarui!");
      } else {
        await addDoc(collection(db, 'regulations'), {
          ...newRegulation,
          year: Number(newRegulation.year),
          downloadCount: 0,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        addNotification("Info Regulasi Baru ⚖️", `Regulasi tahun ${newRegulation.year}: "${newRegulation.title}" telah dirilis.`, 'regulation', 'all');
        toast.success("Regulasi berhasil diunggah!");
      }
      setIsAddRegulationOpen(false);
      setEditingRegulation(null);
      setNewRegulation({
        title: '',
        description: '',
        year: new Date().getFullYear(),
        category: 'Peraturan Pemerintah',
        fileUrl: ''
      });
    } catch (error) {
      console.error('Error adding/updating regulation:', error);
      toast.error("Gagal menyimpan regulasi.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditRegulation = (reg: Regulation) => {
    setEditingRegulation(reg);
    setNewRegulation({
      title: reg.title,
      description: reg.description,
      year: reg.year,
      category: reg.category,
      fileUrl: reg.fileUrl
    });
    setIsAddRegulationOpen(true);
  };

  const [deleteRegDialog, setDeleteRegDialog] = React.useState({ isOpen: false, id: '' });

  const confirmDeleteRegulation = async () => {
    if (!deleteRegDialog.id) return;
    try {
      await deleteDoc(doc(db, 'regulations', deleteRegDialog.id));
      toast.success("Regulasi berhasil dihapus!");
    } catch (error: any) {
      console.error('Error deleting regulation:', error);
      toast.error(`Gagal menghapus regulasi.`);
    } finally {
      setDeleteRegDialog({ isOpen: false, id: '' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-foreground font-bold animate-pulse">Memuat Panel Admin...</div>
      </div>
    );
  }

  if (!isAuthorized) return <div className="pt-32 text-center text-foreground font-bold">Akses Ditolak.</div>;

  return (
    <div className="min-h-screen bg-background pt-32 pb-20 font-sans transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Admin Panel</h1>
            <p className="text-foreground/60">Kelola pesanan, produk, dan pengaturan sistem.</p>
          </div>
          <Dialog open={isAddProductOpen} onOpenChange={(open) => {
            setIsAddProductOpen(open);
            if (!open) {
              setEditingProduct(null);
              setNewProduct({
                name: '',
                category: 'document',
                type: '',
                price: '',
                desc: '',
                image: `https://picsum.photos/seed/${Math.random().toString().slice(2,8)}/400/300`,
                previewImage: '',
                appLink: ''
              });
            }
          }}>
            <DialogTrigger 
              nativeButton={true}
              render={<button type="button" className="inline-flex items-center justify-center bg-gold text-navy hover:bg-gold-light font-bold shadow-lg shadow-gold/20 leading-none h-11 px-6 rounded-xl" />}
            >
              <Plus className="h-4 w-4 mr-2" />
              Tambah Produk Baru
            </DialogTrigger>
            <DialogContent className="sm:max-w-[620px] max-h-[90vh] overflow-y-auto border-none shadow-2xl rounded-[2rem] p-0 bg-card">
              <div className="bg-primary p-6 pt-8">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-primary-foreground">{editingProduct ? 'Edit Produk' : 'Tambah Produk Baru'}</DialogTitle>
                  <DialogDescription className="text-primary-foreground/60">
                    {editingProduct ? 'Perbarui detail produk atau layanan digital Anda.' : 'Isi detail produk atau layanan digital baru untuk ditambahkan ke toko Anda.'}
                  </DialogDescription>
                </DialogHeader>
              </div>
              <form onSubmit={handleAddProduct} className="p-6 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-foreground font-bold">Nama Produk</Label>
                  <Input 
                    id="name" 
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                    placeholder="Contoh: Template RAB Jalan" 
                    className="border-foreground/10 focus:border-primary rounded-xl bg-card text-foreground"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="category" className="text-foreground font-bold">Kategori</Label>
                    <select 
                      id="category"
                      value={newProduct.category}
                      onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
                      className="flex h-10 w-full items-center justify-between rounded-xl border border-foreground/10 bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="document">Dokumen / Template</option>
                      <option value="application">Aplikasi / Software</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="price" className="text-foreground font-bold">Harga (Rp)</Label>
                    <Input 
                      id="price" 
                      type="number"
                      value={newProduct.price}
                      onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                      placeholder="150000" 
                      className="border-foreground/10 focus:border-primary rounded-xl bg-card text-foreground"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type" className="text-foreground font-bold">Tipe Tag</Label>
                  <Input 
                    id="type" 
                    value={newProduct.type}
                    onChange={(e) => setNewProduct({...newProduct, type: e.target.value})}
                    placeholder="Contoh: RAB, Excel, Web" 
                    className="border-foreground/10 focus:border-primary rounded-xl bg-card text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="desc" className="text-foreground font-bold">Deskripsi Produk</Label>
                    <span className="text-[11px] text-foreground/50">Mendukung susunan poin (-), angka (1.), & judul</span>
                  </div>
                  <Tabs defaultValue="write" className="w-full">
                    <div className="flex flex-wrap items-center justify-between gap-1.5 mb-2">
                      <TabsList className="bg-muted p-0.5 rounded-lg h-7">
                        <TabsTrigger value="write" className="text-xs h-6 px-2.5">Tulis</TabsTrigger>
                        <TabsTrigger value="preview" className="text-xs h-6 px-2.5">Pratinjau Rapi</TabsTrigger>
                      </TabsList>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-6 px-2 text-[10px] rounded-lg border-foreground/10"
                          onClick={() => {
                            const val = newProduct.desc ? newProduct.desc + '\n\nFitur Utama:\n' : 'Fitur Utama:\n';
                            setNewProduct({ ...newProduct, desc: val });
                          }}
                        >
                          + Judul:
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-6 px-2 text-[10px] rounded-lg border-foreground/10"
                          onClick={() => {
                            const val = newProduct.desc ? newProduct.desc + '\n- ' : '- ';
                            setNewProduct({ ...newProduct, desc: val });
                          }}
                        >
                          + Poin (-)
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-6 px-2 text-[10px] rounded-lg border-foreground/10"
                          onClick={() => {
                            const val = newProduct.desc ? newProduct.desc + '\n1. ' : '1. ';
                            setNewProduct({ ...newProduct, desc: val });
                          }}
                        >
                          + Angka (1.)
                        </Button>
                      </div>
                    </div>
                    <TabsContent value="write" className="mt-0">
                      <textarea 
                        id="desc"
                        value={newProduct.desc}
                        onChange={(e) => setNewProduct({...newProduct, desc: e.target.value})}
                        className="w-full min-h-[140px] border border-foreground/10 rounded-xl p-3 text-sm focus:outline-none focus:border-primary bg-card text-foreground font-mono leading-relaxed"
                        placeholder="Tulis deskripsi dengan susunan rapi, contoh:&#10;&#10;Aplikasi Manajemen Proyek Terpadu&#10;&#10;Fitur Utama:&#10;- Pendapatan vs Pengeluaran&#10;- Laporan Kas & Slip Gaji&#10;- Rekap Transaksi Harian&#10;&#10;Keunggulan:&#10;1. Format siap pakai&#10;2. Ekspor ke Excel & PDF"
                      />
                    </TabsContent>
                    <TabsContent value="preview" className="mt-0">
                      <div className="min-h-[140px] max-h-[240px] overflow-y-auto border border-foreground/10 rounded-xl p-4 bg-muted/20">
                        <ProductDescriptionRenderer content={newProduct.desc} showBadgeNotice={false} />
                      </div>
                    </TabsContent>
                  </Tabs>
                  <p className="text-[11px] text-foreground/50 leading-relaxed">
                    💡 <strong>Tips Susunan Rapi:</strong> Pisahkan paragraf dengan Enter, awali baris dengan tanda strip (<code className="text-primary">-</code>) untuk checklist poin, atau angka (<code className="text-primary">1.</code>) untuk langkah berurutan.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="image" className="text-foreground font-bold">URL Gambar Katalog</Label>
                  <Input 
                    id="image" 
                    value={newProduct.image}
                    onChange={(e) => setNewProduct({...newProduct, image: e.target.value})}
                    placeholder="https://..." 
                    className="border-foreground/10 focus:border-primary rounded-xl bg-card text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="previewImage" className="text-foreground font-bold">URL Preview Dokumen / Screenshot Aplikasi (Gambar/PDF)</Label>
                  <Input 
                    id="previewImage" 
                    value={newProduct.previewImage}
                    onChange={(e) => setNewProduct({...newProduct, previewImage: e.target.value})}
                    placeholder="https://... (URL Gambar atau PDF)" 
                    className="border-foreground/10 focus:border-primary rounded-xl bg-card text-foreground"
                  />
                </div>
                {newProduct.category === 'application' && (
                  <div className="space-y-2">
                    <Label htmlFor="appLink" className="text-foreground font-bold italic text-primary">Link Web Aplikasi (Penting untuk Produk Aplikasi)</Label>
                    <Input 
                      id="appLink" 
                      value={newProduct.appLink}
                      onChange={(e) => setNewProduct({...newProduct, appLink: e.target.value})}
                      placeholder="https://app.anda.com" 
                      className="border-primary/30 focus:border-primary rounded-xl bg-card text-foreground"
                    />
                  </div>
                )}
                <DialogFooter className="pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsAddProductOpen(false)} className="rounded-xl font-bold border-foreground/10 text-foreground">Batal</Button>
                  <Button type="submit" disabled={isSubmitting} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl font-bold shadow-lg">
                    {isSubmitting ? "Menyimpan..." : (editingProduct ? "Perbarui Produk" : "Simpan Produk")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={isEditFileUrlOpen} onOpenChange={setIsEditFileUrlOpen}>
            <DialogContent className="sm:max-w-[400px] bg-card rounded-[2rem] border-none shadow-2xl p-6">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-foreground">Set Lampiran File</DialogTitle>
                <DialogDescription className="text-foreground/60 text-sm">
                  Masukkan link atau URL untuk file yang dapat diunduh oleh pelanggan.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="fileInput" className="font-bold">Upload File</Label>
                  <Input 
                    id="fileInput"
                    type="file"
                    onChange={handleFileUpload}
                    disabled={isUploading}
                    className="border-foreground/10 rounded-xl"
                  />
                  {isUploading && <p className="text-sm text-yellow-600 font-bold">Mengunggah file... Mohon jangan tutup jendela ini.</p>}
                  {editingFileUrlOrder?.fileUrl && !isUploading && (
                    <p className="text-sm text-green-600 font-bold flex items-center">
                      <CheckCircle2 className="h-4 w-4 mr-2" /> File sudah terunggah!
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fileUrl" className="font-bold">Atau URL Download</Label>
                  <Input 
                    id="fileUrl"
                    value={editingFileUrlOrder?.fileUrl || ''}
                    onChange={(e) => setEditingFileUrlOrder(prev => prev ? {...prev, fileUrl: e.target.value} : null)}
                    placeholder="https://..." 
                    className="border-foreground/10 rounded-xl"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsEditFileUrlOpen(false)} className="rounded-xl font-bold">Batal</Button>
                <Button 
                  onClick={() => {
                    if (editingFileUrlOrder) {
                      updateOrderFields(editingFileUrlOrder.id, { fileUrl: editingFileUrlOrder.fileUrl });
                      setIsEditFileUrlOpen(false);
                      setEditingFileUrlOrder(null);
                    }
                  }}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-xl"
                >
                  Simpan Link
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-card border border-border p-1 rounded-xl sm:rounded-2xl mb-6 md:mb-8 grid grid-cols-3 min-[480px]:grid-cols-4 md:flex gap-1 mx-auto shadow-sm max-w-full !h-auto min-h-[auto] flex-wrap">
            <TabsTrigger value="orders" className="rounded-lg sm:rounded-xl data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md font-bold px-1 md:px-6 py-2 md:py-3 transition-all text-[10px] md:text-sm flex flex-col md:flex-row items-center justify-center gap-1 !h-auto">
              <ShoppingBag className="h-4 w-4 md:mr-0" /> <span className="leading-tight text-center">Pesanan</span>
            </TabsTrigger>
            <TabsTrigger value="products" className="rounded-lg sm:rounded-xl data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md font-bold px-1 md:px-6 py-2 md:py-3 transition-all text-[10px] md:text-sm flex flex-col md:flex-row items-center justify-center gap-1 !h-auto">
              <Package className="h-4 w-4 md:mr-0" /> <span className="leading-tight text-center">Produk</span>
            </TabsTrigger>
            <TabsTrigger value="articles" className="rounded-lg sm:rounded-xl data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md font-bold px-1 md:px-6 py-2 md:py-3 transition-all text-[10px] md:text-sm flex flex-col md:flex-row items-center justify-center gap-1 !h-auto">
              <Newspaper className="h-4 w-4 md:mr-0" /> <span className="leading-tight text-center">Artikel</span>
            </TabsTrigger>
            <TabsTrigger value="regulations" className="rounded-lg sm:rounded-xl data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md font-bold px-1 md:px-6 py-2 md:py-3 transition-all text-[10px] md:text-sm flex flex-col md:flex-row items-center justify-center gap-1 !h-auto">
              <FileText className="h-4 w-4 md:mr-0" /> <span className="leading-tight text-center">Regulasi</span>
            </TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-lg sm:rounded-xl data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md font-bold px-1 md:px-6 py-2 md:py-3 transition-all text-[10px] md:text-sm flex flex-col md:flex-row items-center justify-center gap-1 !h-auto">
              <Star className="h-4 w-4 md:mr-0" /> <span className="leading-tight text-center">Ulasan</span>
            </TabsTrigger>
            <TabsTrigger value="faqs" className="rounded-lg sm:rounded-xl data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md font-bold px-1 md:px-6 py-2 md:py-3 transition-all text-[10px] md:text-sm flex flex-col md:flex-row items-center justify-center gap-1 !h-auto">
              <HelpCircle className="h-4 w-4 md:mr-0" /> <span className="leading-tight text-center">FAQ</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-lg sm:rounded-xl data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md font-bold px-1 md:px-6 py-2 md:py-3 transition-all text-[10px] md:text-sm flex flex-col md:flex-row items-center justify-center gap-1 !h-auto">
              <Users className="h-4 w-4 md:mr-0" /> <span className="leading-tight text-center">Pengguna</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg sm:rounded-xl data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md font-bold px-1 md:px-6 py-2 md:py-3 transition-all text-[10px] md:text-sm flex flex-col md:flex-row items-center justify-center gap-1 !h-auto">
              <Settings className="h-4 w-4 md:mr-0" /> <span className="leading-tight text-center">Pengaturan</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="orders">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-8 md:mb-10">
              {[
                { title: "Total Pesanan", value: stats.totalOrders, icon: ShoppingBag, color: "text-blue-600", bg: "bg-blue-50" },
                { title: "Pesanan Pending", value: stats.pendingOrders, icon: Clock, color: "text-yellow-600", bg: "bg-yellow-50" },
                { title: "Total Pendapatan", value: `Rp ${stats.totalRevenue.toLocaleString('id-ID')}`, icon: CheckCircle2, color: "text-green-600", bg: "bg-green-50" },
                { title: "Katalog Produk", value: stats.totalProducts, icon: Package, color: "text-purple-600", bg: "bg-purple-50" }
              ].map((stat, i) => (
                <Card key={i} className="border-none shadow-md overflow-hidden bg-card transition-colors duration-200">
                  <CardContent className="p-3 sm:p-6 flex items-center gap-3 sm:gap-4">
                    <div className={`h-10 w-10 sm:h-12 sm:w-12 ${stat.bg} rounded-xl flex items-center justify-center shrink-0`}>
                      <stat.icon className={`h-5 w-5 sm:h-6 sm:w-6 ${stat.color}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[9px] sm:text-xs text-foreground/70 font-bold uppercase tracking-tight sm:tracking-wider leading-tight mb-1 truncate">{stat.title}</p>
                      <p className="text-[13px] sm:text-xl font-black text-foreground truncate leading-none">{stat.value}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Recent Orders Table */}
            <Card className="border-none shadow-xl overflow-hidden bg-card">
              <CardHeader className="border-b border-border">
                <CardTitle className="text-lg font-bold text-foreground">Kelola Pesanan</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[600px] w-full border-t border-border">
                  {/* Mobile View */}
                  <div className="md:hidden divide-y divide-border">
                    {orders.map((order) => (
                      <div key={order.id} className="p-4 space-y-4 bg-card hover:bg-muted/50 transition-colors">
                        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                          <div>
                            <div className="font-bold text-foreground line-clamp-2">{order.productName}</div>
                            <div className="text-[10px] text-foreground/60 mb-2">ID: {order.id}</div>
                            <div className="text-sm font-bold text-foreground">{order.userName || 'User'}</div>
                            <div className="text-xs text-foreground/70">{order.userEmail}</div>
                            {order.userPhone && (
                              <div className="flex flex-col gap-1 mt-1">
                                <a 
                                  href={`https://api.whatsapp.com/send?phone=${formatWhatsAppNumber(order.userPhone)}&text=${encodeURIComponent(`Halo ${order.userName}, mohon kirimkan bukti pembayaran untuk pesanan *${order.productName}* agar bisa kami proses.`)}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center text-[10px] font-bold text-green-600 hover:text-green-700"
                                >
                                  <Phone className="h-3 w-3 mr-1" /> Hubungi WA
                                </a>
                                {(order.status === 'awaiting_confirmation') && (
                                  <a 
                                    href={`https://api.whatsapp.com/send?phone=${formatWhatsAppNumber(order.userPhone)}&text=${encodeURIComponent(`Halo ${order.userName}, mohon kirimkan bukti pembayaran untuk pesanan ${order.productName} agar bisa kami proses.`)}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center text-[10px] font-bold text-purple-600 hover:text-purple-700"
                                  >
                                    <MessageCircle className="h-3 w-3 mr-1" /> Minta Bukti Pembayaran
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="text-left sm:text-right w-full sm:w-auto">
                            <div className="text-sm font-bold text-primary mb-2">Rp {order.amount?.toLocaleString('id-ID')}</div>
                            <div className="flex items-center sm:justify-end gap-2 shrink-0 flex-wrap">
                              <Badge className={
                              order.status === 'completed' ? 'bg-green-500' : 
                              order.status === 'processing' ? 'bg-blue-500' : 
                              order.status === 'awaiting_confirmation' ? 'bg-purple-600 animate-pulse' :
                              order.status === 'pending' ? 'bg-yellow-500' : 'bg-red-500'
                            }>
                                {order.status === 'awaiting_confirmation' ? 'Sudah Bayar (Cek DANA)' : order.status}
                              </Badge>
                            </div>
                            <div className="text-xs text-foreground/60 mt-2">
                              {formatDateSafe(order.createdAt, { day: 'numeric', month: 'short', year: 'numeric' })}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap justify-end gap-2 pt-3 border-t border-border">
                          {order.status === 'completed' && (
                            <>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-green-600 text-green-600 hover:bg-green-50/10 font-bold"
                                onClick={() => sendWhatsAppConfirmation(order)}
                              >
                                <Phone className="h-4 w-4 mr-1" /> Kirim WA
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-red-600 text-red-600 hover:bg-red-50/10 shrink-0 px-2"
                                onClick={() => deleteOrder(order.id)}
                                title="Hapus Pesanan"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {(order.status === 'pending' || order.status === 'awaiting_confirmation') && (
                            <Button 
                              size="sm" 
                              className={`flex-1 sm:flex-none ${order.status === 'awaiting_confirmation' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-green-600 hover:bg-green-700'} text-white font-bold text-xs px-2 sm:px-3`}
                              onClick={() => updateOrderStatus(order.id, 'completed')}
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" /> {order.status === 'awaiting_confirmation' ? 'Konfirmasi' : 'Konfirmasi'}
                            </Button>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger
                              nativeButton={true}
                              render={
                                <button type="button" className="inline-flex items-center justify-center hover:bg-muted text-foreground h-8 w-8 rounded-md border border-border" />
                              }
                            >
                              <MoreVertical className="h-4 w-4" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-card border-border">
                              <DropdownMenuItem className="text-foreground hover:bg-muted" onClick={() => {
                                setSelectedReceiptOrder(order);
                                setIsReceiptModalOpen(true);
                              }}>
                                <Printer className="mr-2 h-4 w-4 text-primary" /> Cetak / Lihat Kuitansi Resmi
                              </DropdownMenuItem>
                              <DropdownMenuItem className="text-foreground hover:bg-muted" onClick={() => { setEditingFileUrlOrder(order); setIsEditFileUrlOpen(true); }}>
                                <Package className="mr-2 h-4 w-4 text-primary" /> Kelola Link File
                              </DropdownMenuItem>
                              <DropdownMenuItem className="text-foreground hover:bg-muted" onClick={() => updateOrderStatus(order.id, 'processing')}>
                                <Clock className="mr-2 h-4 w-4 text-blue-500" /> Tandai Diproses
                              </DropdownMenuItem>
                              <DropdownMenuItem className="text-foreground hover:bg-muted" onClick={() => updateOrderStatus(order.id, 'completed')}>
                                <CheckCircle2 className="mr-2 h-4 w-4 text-green-500" /> Tandai Selesai
                              </DropdownMenuItem>
                              <DropdownMenuItem className="text-foreground hover:bg-muted" onClick={() => updateOrderStatus(order.id, 'cancelled')}>
                                <XCircle className="mr-2 h-4 w-4 text-red-500" /> Batalkan
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop View */}
                  <div className="hidden md:block">
                    <table className="w-full text-left">
                      <thead className="bg-muted text-foreground/70 text-xs uppercase font-bold sticky top-0 z-10">
                        <tr>
                          <th className="px-6 py-4">Produk</th>
                          <th className="px-6 py-4">Pelanggan</th>
                          <th className="px-6 py-4">Harga</th>
                          <th className="px-6 py-4">Status</th>
                          <th className="px-6 py-4">Tanggal</th>
                          <th className="px-6 py-4 text-right">Aksi</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {orders.map((order) => (
                          <tr key={order.id} className="hover:bg-muted/50 transition-colors">
                            <td className="px-6 py-4">
                              <div className="font-bold text-foreground text-sm">{order.productName}</div>
                              <div className="text-[10px] text-foreground/60">ID: {order.id}</div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm font-bold text-foreground">{order.userName || 'User'}</div>
                              <div className="text-xs text-foreground/60">{order.userEmail}</div>
                              {order.userPhone && (
                                <div className="flex flex-col gap-1 mt-1">
                                  <a 
                                    href={`https://api.whatsapp.com/send?phone=${formatWhatsAppNumber(order.userPhone)}&text=${encodeURIComponent(`Halo ${order.userName}, mohon kirimkan bukti pembayaran untuk pesanan *${order.productName}* agar bisa kami proses.`)}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center text-[10px] font-bold text-green-600 hover:text-green-700"
                                  >
                                    <Phone className="h-3 w-3 mr-1" /> Hubungi WA
                                  </a>
                                  {(order.status === 'awaiting_confirmation') && (
                                    <a 
                                      href={`https://api.whatsapp.com/send?phone=${formatWhatsAppNumber(order.userPhone)}&text=${encodeURIComponent(`Halo ${order.userName}, mohon kirimkan bukti pembayaran untuk pesanan ${order.productName} agar bisa kami proses.`)}`} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center text-[10px] font-bold text-purple-600 hover:text-purple-700"
                                    >
                                      <MessageCircle className="h-3 w-3 mr-1" /> Minta Bukti Pembayaran
                                    </a>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm font-bold text-primary">Rp {order.amount?.toLocaleString('id-ID')}</div>
                            </td>
                            <td className="px-6 py-4">
                              <Badge className={
                                order.status === 'completed' ? 'bg-green-500' : 
                                order.status === 'processing' ? 'bg-blue-500' : 
                                order.status === 'awaiting_confirmation' ? 'bg-purple-600 animate-pulse' :
                                order.status === 'pending' ? 'bg-yellow-500' : 'bg-red-500'
                              }>
                                {order.status === 'awaiting_confirmation' ? 'Sudah Bayar (Cek DANA)' : order.status}
                              </Badge>
                            </td>
                            <td className="px-6 py-4 text-sm text-foreground/60">
                              {formatDateSafe(order.createdAt, { day: 'numeric', month: 'short', year: 'numeric' })}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end gap-2">
                                {order.status === 'completed' && (
                                  <>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      className="border-green-600 text-green-600 hover:bg-green-50/10 font-bold"
                                      onClick={() => sendWhatsAppConfirmation(order)}
                                    >
                                      <Phone className="h-4 w-4 mr-1" /> Kirim WA Selesai
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      className="border-red-600 text-red-600 hover:bg-red-50/10 shrink-0 px-2"
                                      onClick={() => deleteOrder(order.id)}
                                      title="Hapus Pesanan"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                                {(order.status === 'pending' || order.status === 'awaiting_confirmation') && (
                                  <Button 
                                    size="sm" 
                                    className={`${order.status === 'awaiting_confirmation' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-green-600 hover:bg-green-700'} text-white font-bold`}
                                    onClick={() => updateOrderStatus(order.id, 'completed')}
                                  >
                                    <CheckCircle2 className="h-4 w-4 mr-1" /> {order.status === 'awaiting_confirmation' ? 'Konfirmasi & Selesai' : 'Konfirmasi'}
                                  </Button>
                                )}
                                <DropdownMenu>
                                  <DropdownMenuTrigger
                                    nativeButton={true}
                                    render={
                                      <button type="button" className="inline-flex items-center justify-center hover:bg-muted text-foreground h-8 w-8 rounded-md border border-border" />
                                    }
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="bg-card border-border">
                                    <DropdownMenuItem className="text-foreground hover:bg-muted" onClick={() => {
                                      setSelectedReceiptOrder(order);
                                      setIsReceiptModalOpen(true);
                                    }}>
                                      <Printer className="mr-2 h-4 w-4 text-primary" /> Cetak / Lihat Kuitansi Resmi
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className="text-foreground hover:bg-muted" onClick={() => { setEditingFileUrlOrder(order); setIsEditFileUrlOpen(true); }}>
                                      <Package className="mr-2 h-4 w-4 text-primary" /> Kelola Link File
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className="text-foreground hover:bg-muted" onClick={() => updateOrderStatus(order.id, 'processing')}>
                                      <Clock className="mr-2 h-4 w-4 text-blue-500" /> Tandai Diproses
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className="text-foreground hover:bg-muted" onClick={() => updateOrderStatus(order.id, 'completed')}>
                                      <CheckCircle2 className="mr-2 h-4 w-4 text-green-500" /> Tandai Selesai
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className="text-foreground hover:bg-muted" onClick={() => updateOrderStatus(order.id, 'cancelled')}>
                                      <XCircle className="mr-2 h-4 w-4 text-red-500" /> Batalkan
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {orders.length === 0 && (
                    <div className="text-center py-20">
                      <ShoppingBag className="h-12 w-12 text-foreground/20 mx-auto mb-4" />
                      <p className="text-foreground/60 font-medium">Belum ada pesanan masuk.</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="products">
            <Card className="border-none shadow-xl bg-card overflow-hidden rounded-[2rem]">
              <CardHeader className="bg-muted border-b border-border px-4 sm:px-6 md:px-8 pt-6 md:pt-8 pb-4 md:pb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-xl font-bold text-foreground">Katalog Produk</CardTitle>
                  <CardDescription className="text-foreground/70">Kelola produk dan layanan yang tampil di toko Anda.</CardDescription>
                </div>
              </CardHeader>
              <CardContent className="p-4 sm:px-6 md:p-8">
                {products.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="h-16 w-16 text-foreground/20 mx-auto mb-4" />
                    <h3 className="text-lg font-bold text-foreground mb-2">Belum Ada Produk</h3>
                    <p className="text-foreground/60 mb-6 max-w-md mx-auto">Anda belum menambahkan produk apa pun ke toko. Mulai menambahkan produk pertama Anda.</p>
                    <Button onClick={() => setIsAddProductOpen(true)} className="bg-gold text-navy hover:bg-gold-light font-bold rounded-xl shadow-lg">
                      <Plus className="h-4 w-4 mr-2" /> Tambah Produk
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {products.map((product) => (
                      <div key={product.id} className="group relative bg-card border border-border rounded-2xl overflow-hidden hover:shadow-xl hover:shadow-foreground/5 transition-all">
                        <div className="aspect-video bg-muted overflow-hidden relative">
                          <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          <Badge className="absolute top-3 left-3 bg-card/90 text-foreground backdrop-blur-sm border-none shadow-sm">{product.category}</Badge>
                        </div>
                        <div className="p-5">
                          <div className="mb-3">
                            <h3 className="font-bold text-foreground text-lg line-clamp-1 mb-1">{product.name}</h3>
                            {product.type && (
                              <Badge variant="outline" className="text-[10px] bg-muted text-blue-600 border-blue-100 py-0 h-5 px-2">
                                {product.type}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-foreground/60 line-clamp-2 mb-4 h-10">{product.desc}</p>
                          <div className="flex items-center justify-between mt-auto">
                            <p className="font-black text-primary text-lg">Rp {product.price?.toLocaleString('id-ID') || 0}</p>
                            <div className="flex gap-2">
                              <Button variant="ghost" size="icon" onClick={() => handleEditProduct(product)} className="text-blue-500 hover:text-blue-600 hover:bg-blue-50/10 rounded-xl">
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteProduct(product.id)} className="text-red-500 hover:text-red-600 hover:bg-red-50/10 rounded-xl">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="articles">
            <Card className="border-none shadow-xl bg-card overflow-hidden rounded-[2rem]">
              <CardHeader className="bg-muted border-b border-border px-4 sm:px-6 md:px-8 pt-6 md:pt-8 pb-4 md:pb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-xl font-bold text-foreground">Manajemen Artikel</CardTitle>
                  <CardDescription className="text-foreground/40">Tulis dan kelola artikel atau berita terbaru.</CardDescription>
                </div>
                <Button onClick={() => setIsAddArticleOpen(true)} className="bg-gold text-navy hover:bg-gold-light font-bold rounded-xl shadow-lg">
                  <Plus className="h-4 w-4 mr-2" /> Tulis Artikel
                </Button>
              </CardHeader>
              <CardContent className="p-4 sm:px-6 md:p-8">
                {articles.length === 0 ? (
                  <div className="text-center py-12">
                    <Newspaper className="h-16 w-16 text-foreground/20 mx-auto mb-4" />
                    <h3 className="text-lg font-bold text-foreground mb-2">Belum Ada Artikel</h3>
                    <p className="text-foreground/60 mb-6 max-w-md mx-auto">Mulailah menulis artikel pertama Anda untuk dibagikan kepada pengunjung.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {articles.map((article) => (
                      <div key={article.id} className="group relative bg-card border border-border rounded-2xl overflow-hidden hover:shadow-xl hover:shadow-foreground/5 transition-all">
                        <div className="aspect-video bg-muted overflow-hidden relative">
                          <img src={article.imageUrl} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          <Badge className={`absolute top-3 left-3 ${article.status === 'published' ? 'bg-green-500' : 'bg-yellow-500'} text-white border-none shadow-sm`}>
                            {article.status === 'published' ? 'Published' : 'Draft'}
                          </Badge>
                        </div>
                        <div className="p-5">
                          <h3 className="font-bold text-foreground text-lg line-clamp-2 mb-2 h-14">{article.title}</h3>
                          <p className="text-xs text-foreground/60 mb-4">{article.category} • {formatDateSafe(article.createdAt, { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                          <div className="flex items-center justify-between mt-auto">
                            <div className="flex gap-2">
                              <Button variant="ghost" size="sm" onClick={() => handleEditArticle(article)} className="text-blue-500 hover:text-blue-600 hover:bg-blue-50/10 rounded-xl">
                                <Edit className="h-4 w-4 mr-1" /> Edit
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setDeleteArticleDialog({ isOpen: true, id: article.id })} className="text-red-500 hover:text-red-600 hover:bg-red-50/10 rounded-xl">
                                <Trash2 className="h-4 w-4 mr-1" /> Hapus
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="regulations">
            <Card className="border-none shadow-xl bg-card overflow-hidden rounded-[2rem]">
              <CardHeader className="bg-muted border-b border-border px-4 sm:px-6 md:px-8 pt-6 md:pt-8 pb-4 md:pb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-xl font-bold text-foreground">Manajemen Regulasi</CardTitle>
                  <CardDescription className="text-foreground/40">Upload dan kelola dokumen peraturan atau regulasi terbaru.</CardDescription>
                </div>
                <Button onClick={() => setIsAddRegulationOpen(true)} className="bg-gold text-navy hover:bg-gold-light font-bold rounded-xl shadow-lg">
                  <Plus className="h-4 w-4 mr-2" /> Tambah Regulasi
                </Button>
              </CardHeader>
              <CardContent className="p-4 sm:px-6 md:p-8">
                {regulations.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-16 w-16 text-foreground/20 mx-auto mb-4" />
                    <h3 className="text-lg font-bold text-foreground mb-2">Belum Ada Regulasi</h3>
                    <p className="text-foreground/60 mb-6 max-w-md mx-auto">Upload dokumen regulasi pertama Anda agar pengunjung dapat mendownloadnya.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {regulations.map((reg) => (
                      <div key={reg.id} className="group relative bg-card border border-border rounded-2xl overflow-hidden hover:shadow-xl hover:shadow-foreground/5 transition-all">
                        <div className="p-5">
                          <div className="flex items-start justify-between mb-3">
                            <div className="p-2 rounded-lg bg-gold/10 text-gold">
                              <FileText className="h-5 w-5" />
                            </div>
                            <Badge variant="outline" className="text-[10px]">{reg.category}</Badge>
                          </div>
                          <h3 className="font-bold text-foreground text-lg line-clamp-2 mb-2 h-14">{reg.title}</h3>
                          <p className="text-xs text-foreground/60 line-clamp-2 mb-4 h-8">{reg.description}</p>
                          <div className="flex items-center justify-between mt-auto pt-4 border-t border-border">
                            <div className="text-[10px] text-foreground/40">
                              Tahun: <span className="font-bold text-foreground">{reg.year}</span> • 
                              Downloads: <span className="font-bold text-foreground">{reg.downloadCount || 0}</span>
                            </div>
                            <div className="flex gap-2">
                              <Button variant="ghost" size="sm" onClick={() => handleEditRegulation(reg)} className="text-blue-500 hover:text-blue-600 hover:bg-blue-50/10 rounded-xl px-2 h-8">
                                <Edit className="h-3.5 w-3.5 mr-1" /> Edit
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setDeleteRegDialog({ isOpen: true, id: reg.id })} className="text-red-500 hover:text-red-600 hover:bg-red-50/10 rounded-xl px-2 h-8">
                                <Trash2 className="h-3.5 w-3.5 mr-1" /> Hapus
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8">
              <Card className="border-none shadow-xl rounded-[2rem] overflow-hidden bg-card">
                <CardHeader className="bg-muted border-b border-border px-4 sm:px-6 md:px-8 pt-6 md:pt-8 pb-4 md:pb-6">
                  <CardTitle className="text-xl font-bold text-foreground flex items-center">
                    <ImageIcon className="h-5 w-5 mr-3 text-gold" /> Logo Website Azman204
                  </CardTitle>
                  <CardDescription className="text-foreground/40">Kelola gambar logo brand Azman204 yang tampil di Navbar, Footer, dan Metadata.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 p-4 sm:px-6 md:p-8">
                  <div className="flex items-center gap-4 p-4 bg-muted/60 rounded-2xl border border-border">
                    <div className="w-16 h-16 rounded-xl border-2 border-gold/40 bg-navy flex items-center justify-center overflow-hidden shrink-0 shadow-inner">
                      <img 
                        src={websiteLogoUrl.trim() || "/logo.png"} 
                        alt="Logo Azman204" 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = "/logo.svg";
                        }}
                      />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-bold text-foreground">Preview Logo Saat Ini</p>
                      <p className="text-xs text-foreground/50">
                        {websiteLogoUrl.trim() ? "Menggunakan URL kustom" : "Menggunakan logo resmi Azman204 (Tersimpan aman di sistem)"}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="websiteLogoUrl" className="text-foreground font-bold">URL Gambar Logo Kustom (Opsional)</Label>
                    <Input 
                      id="websiteLogoUrl"
                      value={websiteLogoUrl}
                      onChange={(e) => setWebsiteLogoUrl(e.target.value)}
                      placeholder="Kosongkan untuk menggunakan logo default (/logo.png)"
                      className="rounded-xl border-border focus:border-primary bg-card text-foreground"
                    />
                    <p className="text-[11px] text-foreground/50 leading-relaxed">
                      Kosongkan kolom ini jika ingin menggunakan logo resmi Azman204 default. Jika mengisi URL, pastikan link gambar bersifat publik dan permanen (bukan link sementara yang bisa kedaluwarsa).
                    </p>
                  </div>

                  <div className="flex gap-2">
                    {websiteLogoUrl && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setWebsiteLogoUrl('')}
                        className="rounded-xl border-border text-foreground/70 hover:text-foreground"
                      >
                        Reset ke Default
                      </Button>
                    )}
                    <Button 
                      onClick={handleSaveWebsiteLogo}
                      disabled={isSavingSettings}
                      className="flex-1 bg-gold hover:bg-gold-light text-navy font-bold h-11 rounded-xl shadow-lg shadow-gold/20"
                    >
                      {isSavingSettings ? "Menyimpan..." : (
                        <>
                          <Save className="h-4 w-4 mr-2" /> Simpan Logo Website
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-none shadow-xl rounded-[2rem] overflow-hidden bg-card">
                <CardHeader className="bg-muted border-b border-border px-4 sm:px-6 md:px-8 pt-6 md:pt-8 pb-4 md:pb-6">
                  <CardTitle className="text-xl font-bold text-foreground flex items-center">
                    <Wallet className="h-5 w-5 mr-3 text-blue-600" /> Konfigurasi DANA Bisnis
                  </CardTitle>
                  <CardDescription className="text-foreground/40">Atur rekening dan nomor untuk penerimaan pembayaran QRIS.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 p-4 sm:px-6 md:p-8">
                  <div className="space-y-2">
                    <Label htmlFor="merchantName" className="text-foreground font-bold">Nama Merchant (Tampil di QRIS)</Label>
                    <Input 
                      id="merchantName"
                      value={paymentSettings.merchantName}
                      onChange={(e) => setPaymentSettings(prev => ({ ...prev, merchantName: e.target.value }))}
                      placeholder="Contoh: AZMAN 204"
                      className="rounded-xl border-border focus:border-primary bg-card text-foreground"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="danaId" className="text-foreground font-bold">Konten QRIS DANA Bisnis (EMVCo String)</Label>
                    <Input 
                      id="danaId"
                      value={paymentSettings.danaBusinessId}
                      onChange={(e) => setPaymentSettings(prev => ({ ...prev, danaBusinessId: e.target.value }))}
                      placeholder="Contoh: 00020101021126570011ID.DANA.WWW..."
                      className="rounded-xl border-border focus:border-primary bg-card text-foreground"
                    />
                    <p className="text-[10px] text-foreground/40 leading-relaxed mt-2 p-3 bg-muted rounded-lg">
                      Buka aplikasi DANA Bisnis, pilih QRIS, lalu cari opsi "Salin Teks QRIS" atau gunakan aplikasi QR Scanner untuk mendapatkan teks dari kode QR Anda. Teks ini biasanya diawali dengan <span className="font-bold">000201</span>.
                    </p>
                  </div>

                  <div className="p-4 bg-blue-500/10 rounded-2xl border border-blue-500/20 flex gap-4">
                    <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-600 leading-relaxed">
                      <p className="font-bold mb-1">Informasi</p>
                      Pastikan Kode Akun DANA Bisnis yang Anda masukkan sudah benar agar pembayaran dari pelanggan dapat masuk ke akun Anda dengan tepat.
                    </div>
                  </div>

                  <Button 
                    onClick={handleSavePaymentSettings}
                    disabled={isSavingSettings}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold h-12 rounded-xl shadow-lg shadow-primary/20 mt-6"
                  >
                    {isSavingSettings ? "Menyimpan..." : (
                      <>
                        <Save className="h-4 w-4 mr-2" /> Simpan Konfigurasi Pembayaran
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-none shadow-xl rounded-[2rem] overflow-hidden bg-card">
                <CardHeader className="bg-muted border-b border-border px-4 sm:px-6 md:px-8 pt-6 md:pt-8 pb-4 md:pb-6">
                  <CardTitle className="text-xl font-bold text-foreground flex items-center">
                    <Package className="h-5 w-5 mr-3 text-primary" /> Banner Promo Produk
                  </CardTitle>
                  <CardDescription className="text-foreground/40">Atur tampilan banner promosi di halaman Produk.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5 md:space-y-6 p-4 sm:px-6 md:p-8">
                  <div className="flex items-center gap-3 p-4 bg-muted rounded-xl border border-border">
                    <input
                      type="checkbox"
                      id="promoProductActive"
                      checked={promoProductSettings.isActive}
                      onChange={(e) => setPromoProductSettings(prev => ({ ...prev, isActive: e.target.checked }))}
                      className="w-5 h-5 rounded border-border text-primary focus:ring-primary"
                    />
                    <Label htmlFor="promoProductActive" className="text-foreground font-bold cursor-pointer pt-0.5">Tampilkan Banner Promo Produk</Label>
                  </div>
                  
                  <div className={promoProductSettings.isActive ? 'opacity-100 space-y-6 transition-opacity' : 'opacity-50 pointer-events-none space-y-6 transition-opacity'}>
                      <div className="space-y-2">
                        <Label className="text-foreground font-bold">URL Gambar Banner</Label>
                        <Input 
                          value={promoProductSettings.bannerUrl}
                          onChange={(e) => setPromoProductSettings(prev => ({ ...prev, bannerUrl: e.target.value }))}
                          placeholder="https://..."
                          className="rounded-xl border-border focus:border-primary bg-card text-foreground"
                        />
                        {promoProductSettings.bannerUrl && (
                           <div className="mt-3 rounded-xl border border-border overflow-hidden h-36 relative bg-muted flex items-center justify-center">
                              <img src={promoProductSettings.bannerUrl} alt="Preview" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                           </div>
                        )}
                      </div>
                  </div>

                  <Button 
                    onClick={handleSavePromoProductSettings} 
                    disabled={isSavingSettings}
                    className="w-full bg-gold hover:bg-gold-light text-navy font-bold h-12 rounded-xl shadow-lg shadow-gold/20 mt-6"
                  >
                    {isSavingSettings ? 'Menyimpan...' : (
                      <><Save className="mr-2 h-4 w-4" /> Simpan Promo Produk</>
                    )}
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-none shadow-xl rounded-[2rem] overflow-hidden bg-card">
                <CardHeader className="bg-muted border-b border-border px-4 sm:px-6 md:px-8 pt-6 md:pt-8 pb-4 md:pb-6">
                  <CardTitle className="text-xl font-bold text-foreground flex items-center">
                    <LayoutDashboard className="h-5 w-5 mr-3 text-blue-600" /> Banner Promo Layanan
                  </CardTitle>
                  <CardDescription className="text-foreground/40">Atur tampilan banner promosi di halaman Layanan.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5 md:space-y-6 p-4 sm:px-6 md:p-8">
                  <div className="flex items-center gap-3 p-4 bg-muted rounded-xl border border-border">
                    <input
                      type="checkbox"
                      id="promoServiceActive"
                      checked={promoServiceSettings.isActive}
                      onChange={(e) => setPromoServiceSettings(prev => ({ ...prev, isActive: e.target.checked }))}
                      className="w-5 h-5 rounded border-border text-blue-600 focus:ring-blue-600"
                    />
                    <Label htmlFor="promoServiceActive" className="text-foreground font-bold cursor-pointer pt-0.5">Tampilkan Banner Promo Layanan</Label>
                  </div>
                  
                  <div className={promoServiceSettings.isActive ? 'opacity-100 space-y-6 transition-opacity' : 'opacity-50 pointer-events-none space-y-6 transition-opacity'}>
                      <div className="space-y-2">
                        <Label className="text-foreground font-bold">URL Gambar Banner</Label>
                        <Input 
                          value={promoServiceSettings.bannerUrl}
                          onChange={(e) => setPromoServiceSettings(prev => ({ ...prev, bannerUrl: e.target.value }))}
                          placeholder="https://..."
                          className="rounded-xl border-border focus:border-primary bg-card text-foreground"
                        />
                        {promoServiceSettings.bannerUrl && (
                           <div className="mt-3 rounded-xl border border-border overflow-hidden h-36 relative bg-muted flex items-center justify-center">
                              <img src={promoServiceSettings.bannerUrl} alt="Preview" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                           </div>
                        )}
                      </div>
                  </div>

                  <Button 
                    onClick={handleSavePromoServiceSettings} 
                    disabled={isSavingSettings}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold h-12 rounded-xl shadow-lg shadow-primary/20 mt-6"
                  >
                    {isSavingSettings ? 'Menyimpan...' : (
                      <><Save className="mr-2 h-4 w-4" /> Simpan Promo Layanan</>
                    )}
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-none shadow-xl rounded-[2rem] overflow-hidden bg-card">
                <CardHeader className="bg-muted border-b border-border px-4 sm:px-6 md:px-8 pt-6 md:pt-8 pb-4 md:pb-6">
                  <CardTitle className="text-xl font-bold text-foreground flex items-center">
                    <Layout className="h-5 w-5 mr-3 text-primary" /> Banner Slider Beranda
                  </CardTitle>
                  <CardDescription className="text-foreground/40">Atur hingga 3 gambar slide untuk halaman utama.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5 md:space-y-6 p-4 sm:px-6 md:p-8">
                  <div className="flex items-center gap-3 p-4 bg-muted rounded-xl border border-border">
                    <input
                      type="checkbox"
                      id="homeSliderActive"
                      checked={homeSliderSettings.isActive}
                      onChange={(e) => setHomeSliderSettings(prev => ({ ...prev, isActive: e.target.checked }))}
                      className="w-5 h-5 rounded border-border text-primary focus:ring-primary"
                    />
                    <Label htmlFor="homeSliderActive" className="text-foreground font-bold cursor-pointer pt-0.5">Aktifkan Slider Beranda</Label>
                  </div>
                  
                  <div className={homeSliderSettings.isActive ? 'opacity-100 space-y-8 transition-opacity' : 'opacity-50 pointer-events-none space-y-8 transition-opacity'}>
                    {homeSliderSettings.banners.map((banner, index) => (
                      <div key={index} className="p-4 border border-border rounded-2xl bg-muted/50 space-y-4">
                        <div className="flex items-center justify-between">
                          <Label className="text-foreground font-black flex items-center">
                            <span className="w-6 h-6 rounded-full bg-gold text-navy flex items-center justify-center text-xs mr-2">{index + 1}</span>
                            Slide Gambar {index + 1}
                          </Label>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={banner.active}
                              onChange={(e) => {
                                const newBanners = [...homeSliderSettings.banners];
                                newBanners[index].active = e.target.checked;
                                setHomeSliderSettings(prev => ({ ...prev, banners: newBanners }));
                              }}
                              className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                            />
                            <span className="text-xs text-foreground/60 font-medium">Aktif</span>
                          </div>
                        </div>
                        <Input 
                          value={banner.url}
                          onChange={(e) => {
                            const newBanners = [...homeSliderSettings.banners];
                            newBanners[index].url = e.target.value;
                            setHomeSliderSettings(prev => ({ ...prev, banners: newBanners }));
                          }}
                          placeholder="https://..."
                          className="rounded-xl border-border focus:border-primary bg-card text-foreground"
                        />
                        {banner.url && (
                          <div className="rounded-xl border border-border overflow-hidden h-32 bg-muted relative">
                            <img src={banner.url} alt={`Slide ${index + 1}`} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <Button 
                    onClick={handleSaveHomeSliderSettings} 
                    disabled={isSavingSettings}
                    className="w-full bg-gold hover:bg-gold-light text-navy font-bold h-12 rounded-xl shadow-lg shadow-gold/20 mt-6"
                  >
                    {isSavingSettings ? 'Menyimpan...' : (
                      <><Save className="mr-2 h-4 w-4" /> Simpan Slider Beranda</>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          <TabsContent value="reviews">
            <Card className="border-none shadow-xl bg-card">
              <CardHeader className="bg-primary/5 border-b border-border flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-lg font-bold text-foreground">Ulasan & Testimoni</CardTitle>
                  <CardDescription className="text-foreground/60">Kelola ulasan dari database `productReviews` yang ditampilkan di Beranda dan detail produk.</CardDescription>
                </div>
                <Dialog open={isAddReviewOpen} onOpenChange={setIsAddReviewOpen}>
                  <DialogTrigger 
                    nativeButton={true}
                    render={<button type="button" className="inline-flex items-center justify-center bg-gold text-navy hover:bg-gold-light font-bold text-xs shadow-lg shadow-gold/20 h-10 px-4 rounded-xl" />}
                  >
                    <Plus className="h-4 w-4 mr-1.5" /> Tambah Ulasan Manual
                  </DialogTrigger>
                  <DialogContent className="bg-card text-foreground border-border max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-lg font-bold text-foreground">Tambah Ulasan Klien</DialogTitle>
                      <DialogDescription className="text-foreground/60">Gunakan form ini untuk memasukkan ulasan manual dari WhatsApp, email, atau klien luar.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleAddReview} className="space-y-4 font-sans text-sm">
                      <div className="space-y-1.5">
                        <Label htmlFor="rev-name" className="text-foreground font-bold">Nama Klien <span className="text-red-500">*</span></Label>
                        <Input 
                          id="rev-name" 
                          required
                          value={newReview.userName} 
                          onChange={(e) => setNewReview({...newReview, userName: e.target.value})}
                          placeholder="Contoh: PT. Adhi Karya Utama"
                          className="bg-background text-foreground border-border rounded-xl"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="rev-email" className="text-foreground">Email Klien (Opsional)</Label>
                        <Input 
                          id="rev-email" 
                          type="email"
                          value={newReview.userEmail} 
                          onChange={(e) => setNewReview({...newReview, userEmail: e.target.value})}
                          placeholder="client@company.com"
                          className="bg-background text-foreground border-border rounded-xl"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="rev-product" className="text-foreground font-bold">Layanan / Produk <span className="text-red-500">*</span></Label>
                        <Input 
                          id="rev-product" 
                          required
                          value={newReview.productName} 
                          onChange={(e) => setNewReview({...newReview, productName: e.target.value})}
                          placeholder="Contoh: Dokumen Penawaran Tender Konstruksi"
                          className="bg-background text-foreground border-border rounded-xl"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="rev-rating" className="text-foreground font-bold font-sans">Rating Bintang <span className="text-red-500">*</span></Label>
                        <select 
                          id="rev-rating"
                          value={newReview.rating}
                          onChange={(e) => setNewReview({...newReview, rating: Number(e.target.value)})}
                          className="w-full bg-background text-foreground border border-border rounded-xl px-3 py-2.5 outline-none focus:border-primary"
                        >
                          <option value="5">⭐⭐⭐⭐⭐ (5 Bintang)</option>
                          <option value="4">⭐⭐⭐⭐ (4 Bintang)</option>
                          <option value="3">⭐⭐⭐ (3 Bintang)</option>
                          <option value="2">⭐⭐ (2 Bintang)</option>
                          <option value="1">⭐ (1 Bintang)</option>
                        </select>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="rev-text" className="text-foreground font-bold">Isi Ulasan <span className="text-red-500">*</span></Label>
                        <textarea 
                          id="rev-text" 
                          required
                          rows={3}
                          value={newReview.reviewText} 
                          onChange={(e) => setNewReview({...newReview, reviewText: e.target.value})}
                          placeholder="Sangat puas dengan dokumen administrasi tender yang profesional dari Azman204..."
                          className="w-full bg-background text-foreground border border-border rounded-xl px-3 py-2 outline-none focus:border-primary"
                        />
                      </div>
                      <DialogFooter className="pt-2">
                        <Button type="button" variant="ghost" onClick={() => setIsAddReviewOpen(false)} className="rounded-xl hover:bg-muted text-foreground">
                          Batal
                        </Button>
                        <Button type="submit" disabled={isSubmitting} className="bg-gold hover:bg-gold-light text-navy font-bold rounded-xl">
                          {isSubmitting ? "Menyimpan..." : "Posting Ulasan"}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent className="p-0">
                <div className="min-h-[400px]">
                  {/* Desktop Table View */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="text-[10px] sm:text-xs text-foreground/45 uppercase bg-muted/50 whitespace-nowrap sticky top-0 z-10 font-bold tracking-widest border-b border-border/30">
                        <tr>
                          <th className="px-6 py-4">Klien & Produk</th>
                          <th className="px-6 py-4">Saran / Rating</th>
                          <th className="px-6 py-4">Pesan Ulasan</th>
                          <th className="px-6 py-4 text-right">Aksi</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {productReviews.map((review) => (
                          <tr key={review.id} className="hover:bg-muted/30 transition-all">
                            <td className="px-6 py-4">
                              <div className="font-bold text-foreground text-sm">{review.userName || 'Klien'}</div>
                              <div className="text-xs text-foreground/50">{review.userEmail || '-'}</div>
                              <div className="text-[10px] text-primary bg-primary/10 mt-1.5 px-2.5 py-0.5 rounded-full w-fit border border-primary/25 font-bold">{review.productName}</div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-1">
                                {[...Array(5)].map((_, i) => (
                                  <Star key={i} className={`h-4 w-4 ${i < (review.rating || 5) ? 'text-gold fill-current' : 'text-foreground/20'}`} />
                                ))}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-xs text-foreground/80 italic max-w-sm whitespace-pre-wrap leading-relaxed">
                                "{review.reviewText || 'Tidak ada teks ulasan'}"
                              </p>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-red-400 hover:text-red-500 hover:bg-red-500/10 rounded-xl"
                                onClick={() => handleDeleteReview(review.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {productReviews.length === 0 && (
                          <tr>
                            <td colSpan={4} className="px-6 py-16 text-center text-foreground/45">
                              <Star className="h-10 w-10 mx-auto mb-3 opacity-20 text-gold" />
                              <p className="font-semibold text-sm">Belum ada ulasan klien</p>
                              <p className="text-xs mt-1">Ulasan manual atau ulasan otomatis produk akan muncul di sini.</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="md:hidden flex flex-col divide-y divide-border">
                    {productReviews.length === 0 ? (
                      <div className="p-12 text-center text-foreground/45 flex flex-col items-center">
                        <Star className="h-10 w-10 mb-3 opacity-20 text-gold" />
                        <span className="text-sm font-semibold">Belum ada ulasan klien.</span>
                      </div>
                    ) : (
                      productReviews.map((review) => (
                        <div key={review.id} className="p-4 flex flex-col gap-3">
                          <div className="flex justify-between items-start gap-2">
                            <div>
                              <div className="font-bold text-foreground text-sm">{review.userName || 'Klien'}</div>
                              <div className="text-xs text-foreground/60">{review.userEmail || '-'}</div>
                            </div>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-400 hover:text-red-500 hover:bg-red-500/10 h-8 w-8 p-0 rounded-full"
                              onClick={() => handleDeleteReview(review.id)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          
                          <div className="text-[10px] text-primary bg-primary/10 px-2 py-0.5 rounded-full w-fit border border-primary/20">{review.productName}</div>
                          
                          <div className="flex items-center space-x-1 mt-1">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className={`h-3.5 w-3.5 ${i < (review.rating || 5) ? 'text-gold fill-current' : 'text-foreground/20'}`} />
                            ))}
                          </div>
                          
                          <p className="text-xs text-foreground/80 italic whitespace-pre-wrap bg-muted/35 p-3 rounded-2xl border border-border mt-1">
                            "{review.reviewText || 'Tidak ada teks ulasan'}"
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="faqs">
            <Card className="border-none shadow-xl bg-card">
              <CardHeader className="bg-primary/5 border-b border-border flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-lg font-bold text-foreground flex items-center gap-2">
                    Tanya Jawab (FAQ) Konstruksi
                    <Badge variant="secondary" className="bg-primary/10 text-primary border-none rounded-full ml-1 font-mono text-xs">
                      {adminFaqs.length}
                    </Badge>
                  </CardTitle>
                  <CardDescription className="text-foreground/60">
                    Kelola daftar tanya-jawab umum di halaman beranda untuk membangun transparansi dan kepercayaan pelanggan.
                  </CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Dialog open={isAddFaqOpen} onOpenChange={(open) => {
                    setIsAddFaqOpen(open);
                    if (!open) {
                      setEditingFaq(null);
                      setNewFaq({
                        question: '',
                        answer: '',
                        category: 'Format Dokumen',
                        order: adminFaqs.length + 1
                      });
                    }
                  }}>
                    <DialogTrigger 
                      nativeButton={true}
                      render={<button type="button" className="inline-flex items-center justify-center bg-gold text-navy hover:bg-gold-light font-bold text-xs shadow-lg shadow-gold/20 h-10 px-4 rounded-xl" />}
                    >
                      <Plus className="h-4 w-4 mr-1.5" /> Tambah Pertanyaan
                    </DialogTrigger>
                    <DialogContent className="bg-card text-foreground border-border max-w-lg">
                      <DialogHeader>
                        <DialogTitle className="text-lg font-bold text-foreground">
                          {editingFaq ? 'Edit Tanya Jawab' : 'Tambah Tanya Jawab Baru'}
                        </DialogTitle>
                        <DialogDescription className="text-foreground/60">
                          Masukkan pertanyaan dan jawaban seputar dokumen, regulasi, pembayaran, atau layanan konsultasi.
                        </DialogDescription>
                      </DialogHeader>
                      <form 
                        onSubmit={async (e) => {
                          e.preventDefault();
                          if (!newFaq.question.trim() || !newFaq.answer.trim()) {
                            toast.error("Pertanyaan dan Jawaban wajib diisi!");
                            return;
                          }
                          setIsSubmitting(true);
                          try {
                            if (editingFaq) {
                              await updateDoc(doc(db, 'faqs', editingFaq.id), {
                                question: newFaq.question,
                                answer: newFaq.answer,
                                category: newFaq.category,
                                order: Number(newFaq.order) || 1,
                                updatedAt: new Date()
                              });
                              toast.success("FAQ berhasil diperbarui!");
                            } else {
                              await addDoc(collection(db, 'faqs'), {
                                question: newFaq.question,
                                answer: newFaq.answer,
                                category: newFaq.category,
                                order: Number(newFaq.order) || (adminFaqs.length + 1),
                                createdAt: new Date(),
                                updatedAt: new Date()
                              });
                              toast.success("FAQ baru berhasil ditambahkan!");
                            }
                            setIsAddFaqOpen(false);
                            setEditingFaq(null);
                            setNewFaq({
                              question: '',
                              answer: '',
                              category: 'Format Dokumen',
                              order: adminFaqs.length + 2
                            });
                          } catch (err: any) {
                            console.error("Gagal menyimpan FAQ:", err);
                            toast.error("Gagal menyimpan FAQ: " + (err.message || ''));
                          } finally {
                            setIsSubmitting(false);
                          }
                        }} 
                        className="space-y-4 font-sans text-sm"
                      >
                        <div className="space-y-1.5">
                          <Label htmlFor="faq-q" className="text-foreground font-bold">Pertanyaan <span className="text-red-500">*</span></Label>
                          <Input 
                            id="faq-q" 
                            required
                            placeholder="Contoh: Format file apa saja yang disediakan?" 
                            value={newFaq.question}
                            onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
                            className="bg-muted/50 border-border"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1.5">
                            <Label htmlFor="faq-cat" className="text-foreground font-bold">Kategori</Label>
                            <select
                              id="faq-cat"
                              value={newFaq.category}
                              onChange={(e) => setNewFaq({ ...newFaq, category: e.target.value })}
                              className="w-full h-10 px-3 rounded-xl bg-muted/50 border border-border text-foreground text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary"
                            >
                              <option value="Format Dokumen">Format Dokumen</option>
                              <option value="Standar & Regulasi">Standar & Regulasi</option>
                              <option value="Pembayaran & Unduh">Pembayaran & Unduh</option>
                              <option value="Konsultasi & Layanan">Konsultasi & Layanan</option>
                              <option value="Dukungan & Garansi">Dukungan & Garansi</option>
                            </select>
                          </div>
                          <div className="space-y-1.5">
                            <Label htmlFor="faq-order" className="text-foreground font-bold">Urutan Tampil</Label>
                            <Input 
                              id="faq-order" 
                              type="number"
                              min="1"
                              value={newFaq.order}
                              onChange={(e) => setNewFaq({ ...newFaq, order: Number(e.target.value) })}
                              className="bg-muted/50 border-border"
                            />
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="faq-ans" className="text-foreground font-bold">Jawaban Lengkap <span className="text-red-500">*</span></Label>
                          <textarea 
                            id="faq-ans" 
                            required
                            rows={4}
                            placeholder="Tuliskan jawaban yang detail, transparan, dan informatif..." 
                            value={newFaq.answer}
                            onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
                            className="w-full p-3 rounded-xl bg-muted/50 border border-border text-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <DialogFooter className="pt-2">
                          <Button 
                            type="button" 
                            variant="ghost" 
                            onClick={() => setIsAddFaqOpen(false)}
                            className="rounded-xl font-bold"
                          >
                            Batal
                          </Button>
                          <Button 
                            type="submit" 
                            disabled={isSubmitting}
                            className="bg-gold text-navy hover:bg-gold-light rounded-xl font-bold"
                          >
                            {isSubmitting ? "Menyimpan..." : (editingFaq ? "Simpan Perubahan" : "Simpan Pertanyaan")}
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="p-4 border-b border-border bg-muted/20 flex flex-wrap items-center justify-between gap-3">
                  <span className="text-xs text-foreground/60 font-medium">
                    Total {adminFaqs.length} tanya jawab aktif di Firestore.
                  </span>
                  {adminFaqs.length === 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs font-bold border-gold/40 text-gold hover:bg-gold/10"
                      onClick={async () => {
                        setIsSubmitting(true);
                        try {
                          const seedList = [
                            { order: 1, category: 'Format Dokumen', question: 'Format file apa saja yang akan saya dapatkan setelah membeli produk konstruksi?', answer: 'Anda akan mendapatkan file dokumen dalam format Microsoft Excel (.xlsx), Microsoft Word (.docx), AutoCAD (.dwg), serta salinan PDF siap cetak. Semua dokumen 100% editable (bebas diedit), tidak dikunci kata sandi (unlocked), dan tabel perhitungan otomatis sudah dilengkapi formula baku yang siap digunakan.' },
                            { order: 2, category: 'Standar & Regulasi', question: 'Apakah dokumen SMKK dan RAB sudah memenuhi regulasi Permen PUPR & LPSE terbaru?', answer: 'Ya, seluruh format Sistem Manajemen Keselamatan Konstruksi (SMKK), Rencana Keselamatan Konstruksi (RKK), Rencana Mutu Pekerjaan Konstruksi (RMPK), dan Analisis Harga Satuan Pekerjaan (AHSP) mengacu ketat pada Permen PUPR No. 10/2021 serta standar evaluasi dokumen penawaran tender LPSE/PBJ konstruksi terbaru.' },
                            { order: 3, category: 'Pembayaran & Unduh', question: 'Bagaimana alur pengunduhan dokumen setelah melakukan pembayaran?', answer: 'Setelah pembayaran Anda dikonfirmasi (melalui transfer bank atau QRIS), status pesanan akan langsung berubah menjadi selesai (completed). File dokumen digital akan seketika aktif dan dapat diunduh tanpa batas waktu melalui menu Dashboard Akun Anda di platform Azman204.' },
                            { order: 4, category: 'Format Dokumen', question: 'Apakah formula dan koefisien dalam file RAB Excel bisa disesuaikan dengan harga lokal?', answer: 'Sangat bisa. Lembar kerja spreadsheet RAB kami terbuka penuh (unlocked). Anda bebas mengganti daftar upah minimum regional (UMR), harga bahan material setempat, biaya sewa alat berat, maupun menambahkan item pekerjaan baru sesuai kebutuhan spesifik proyek Anda.' },
                            { order: 5, category: 'Konsultasi & Layanan', question: 'Apakah Azman204 menerima jasa penyusunan dokumen tender kustom atau review RAB?', answer: 'Tentu saja. Selain produk template siap pakai, tim estimator dan tenaga ahli konstruksi kami siap membantu penyusunan dokumen tender terarah, review kelayakan RAB, pembuatan metode pelaksanaan khusus, hingga asistensi teknis melalui tombol Chat Admin WhatsApp kami.' }
                          ];
                          for (const s of seedList) {
                            await addDoc(collection(db, 'faqs'), {
                              ...s,
                              createdAt: new Date(),
                              updatedAt: new Date()
                            });
                          }
                          toast.success("Berhasil menginisialisasi FAQ konstruksi bawaan!");
                        } catch (err: any) {
                          toast.error("Gagal menginisialisasi: " + (err.message || ''));
                        } finally {
                          setIsSubmitting(false);
                        }
                      }}
                    >
                      Inisialisasi FAQ Konstruksi Bawaan
                    </Button>
                  )}
                </div>

                <div className="divide-y divide-border">
                  {adminFaqs.length === 0 ? (
                    <div className="text-center py-12 text-foreground/40 text-sm">
                      Belum ada data FAQ di Firestore. Klik &ldquo;Tambah Pertanyaan&rdquo; atau inisialisasi bawaan di atas.
                    </div>
                  ) : (
                    adminFaqs.map((faq) => (
                      <div key={faq.id} className="p-4 sm:p-6 hover:bg-muted/30 transition-colors flex flex-col sm:flex-row items-start justify-between gap-4">
                        <div className="space-y-2 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-muted text-foreground/60 font-bold">
                              #{faq.order || 1}
                            </span>
                            <Badge variant="secondary" className="text-[10px] font-bold uppercase tracking-wider bg-gold/10 text-gold border-none">
                              {faq.category || 'Format Dokumen'}
                            </Badge>
                          </div>
                          <h4 className="font-bold text-foreground text-base">
                            {faq.question}
                          </h4>
                          <p className="text-sm text-foreground/70 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingFaq(faq);
                              setNewFaq({
                                question: faq.question || '',
                                answer: faq.answer || '',
                                category: faq.category || 'Format Dokumen',
                                order: faq.order || 1
                              });
                              setIsAddFaqOpen(true);
                            }}
                            className="h-8 px-2.5 rounded-lg text-primary hover:bg-primary/10 font-bold text-xs"
                          >
                            <Edit className="h-3.5 w-3.5 mr-1" /> Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={async () => {
                              try {
                                await deleteDoc(doc(db, 'faqs', faq.id));
                                toast.success("FAQ berhasil dihapus!");
                              } catch (err: any) {
                                toast.error("Gagal menghapus FAQ");
                              }
                            }}
                            className="h-8 w-8 p-0 rounded-lg text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users">
            <Card className="border-none shadow-xl bg-card">
              <CardHeader className="bg-primary/5 border-b border-border">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <CardTitle className="text-lg font-bold text-foreground flex items-center gap-2">
                      Daftar Pengguna Terdaftar
                      <Badge variant="secondary" className="bg-primary/10 text-primary border-none rounded-full ml-1 font-mono text-xs">
                        {users.length}
                      </Badge>
                    </CardTitle>
                    <CardDescription className="text-foreground/60 text-xs sm:text-sm">Lihat profil pengguna yang telah mendaftar di aplikasi ini.</CardDescription>
                  </div>
                  <div className="relative max-w-sm w-full md:w-80">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
                    <Input
                      placeholder="Cari nama, email, perusahaan..."
                      value={userSearchText}
                      onChange={(e) => setUserSearchText(e.target.value)}
                      className="pl-9 bg-muted/30 border-foreground/10 focus:outline-none focus:border-primary rounded-xl text-sm"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="min-h-[400px]">
                  {/* Desktop Table View */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="text-[10px] sm:text-xs text-foreground/40 uppercase bg-muted/50 whitespace-nowrap sticky top-0 z-10 font-bold tracking-widest">
                        <tr>
                          <th className="px-6 py-4">Profil</th>
                          <th className="px-6 py-4">Kontak & Perusahaan</th>
                          <th className="px-6 py-4">Peran (Role)</th>
                          <th className="px-6 py-4 text-right">Tanggal Bergabung</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {users.filter(usr => {
                          const search = userSearchText.toLowerCase();
                          return (usr.displayName || '').toLowerCase().includes(search) ||
                                 (usr.email || '').toLowerCase().includes(search) ||
                                 (usr.companyName || '').toLowerCase().includes(search);
                        }).map((usr) => (
                          <tr key={usr.uid} className="hover:bg-muted/50 transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                {usr.photoURL ? (
                                  <img src={usr.photoURL} alt={usr.displayName} className="h-10 w-10 rounded-full object-cover border border-border" referrerPolicy="no-referrer" />
                                ) : (
                                  <div className="h-10 w-10 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center text-sm border border-primary/20">
                                    {(usr.displayName || usr.email || 'U').charAt(0).toUpperCase()}
                                  </div>
                                )}
                                <div>
                                  <div className="font-bold text-foreground">{usr.displayName || 'Tanpa Nama'}</div>
                                  <div className="text-xs text-foreground/60">{usr.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="space-y-1 text-xs">
                                {usr.phoneNumber && (
                                  <div className="flex items-center gap-1.5 text-foreground/70">
                                    <Phone className="h-3 w-3 shrink-0" />
                                    <span>{usr.phoneNumber}</span>
                                  </div>
                                )}
                                {usr.companyName && (
                                  <div className="flex items-center gap-1.5 text-foreground/90 font-medium">
                                    <Briefcase className="h-3 w-3 shrink-0" />
                                    <span>{usr.companyName}</span>
                                  </div>
                                )}
                                {usr.address && (
                                  <div className="text-foreground/50 max-w-xs truncate">
                                    {usr.address}
                                  </div>
                                )}
                                {!usr.phoneNumber && !usr.companyName && !usr.address && (
                                  <span className="text-foreground/40 italic">Belum melengkapi profil</span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <Badge className={usr.role === 'admin' ? 'bg-red-500/10 text-red-600 border-none' : 'bg-primary/10 text-primary border-none'}>
                                {usr.role === 'admin' ? 'Administrator' : 'Pengguna'}
                              </Badge>
                            </td>
                            <td className="px-6 py-4 text-right text-xs text-foreground/60">
                              {usr.createdAt?.toDate ? usr.createdAt.toDate().toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                            </td>
                          </tr>
                        ))}
                        {users.filter(usr => {
                          const search = userSearchText.toLowerCase();
                          return (usr.displayName || '').toLowerCase().includes(search) ||
                                 (usr.email || '').toLowerCase().includes(search) ||
                                 (usr.companyName || '').toLowerCase().includes(search);
                        }).length === 0 && (
                          <tr>
                            <td colSpan={4} className="px-6 py-12 text-center text-foreground/40">
                              <Users className="h-8 w-8 mx-auto mb-2 opacity-20" />
                              Tidak ada data pengguna yang cocok.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="md:hidden flex flex-col divide-y divide-border">
                    {users.filter(usr => {
                      const search = userSearchText.toLowerCase();
                      return (usr.displayName || '').toLowerCase().includes(search) ||
                             (usr.email || '').toLowerCase().includes(search) ||
                             (usr.companyName || '').toLowerCase().includes(search);
                    }).length === 0 ? (
                      <div className="p-8 text-center text-foreground/40 flex flex-col items-center">
                        <Users className="h-8 w-8 mb-2 opacity-20" />
                        <span className="text-sm">Tidak ada data pengguna yang cocok.</span>
                      </div>
                    ) : (
                      users.filter(usr => {
                        const search = userSearchText.toLowerCase();
                        return (usr.displayName || '').toLowerCase().includes(search) ||
                               (usr.email || '').toLowerCase().includes(search) ||
                               (usr.companyName || '').toLowerCase().includes(search);
                      }).map((usr) => (
                        <div key={usr.uid} className="p-4 flex flex-col gap-3">
                          <div className="flex justify-between items-start gap-2">
                            <div className="flex items-center gap-3">
                              {usr.photoURL ? (
                                <img src={usr.photoURL} alt={usr.displayName} className="h-10 w-10 rounded-full object-cover border border-border" referrerPolicy="no-referrer" />
                              ) : (
                                <div className="h-10 w-10 shrink-0 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center text-sm border border-primary/20">
                                  {(usr.displayName || usr.email || 'U').charAt(0).toUpperCase()}
                                </div>
                              )}
                              <div>
                                <div className="font-bold text-foreground text-sm">{usr.displayName || 'Tanpa Nama'}</div>
                                <div className="text-xs text-foreground/60">{usr.email}</div>
                              </div>
                            </div>
                            <Badge className={usr.role === 'admin' ? 'bg-red-500/10 text-red-600 border-none shrink-0 text-[10px]' : 'bg-primary/10 text-primary border-none shrink-0 text-[10px]'}>
                              {usr.role === 'admin' ? 'Admin' : 'User'}
                            </Badge>
                          </div>

                          <div className="bg-muted/30 p-3 rounded-lg border border-border flex flex-col gap-1.5 text-xs">
                            {usr.phoneNumber && (
                              <div className="flex items-center gap-1.5 text-foreground/70">
                                <Phone className="h-3 w-3 shrink-0" />
                                <span>{usr.phoneNumber}</span>
                              </div>
                            )}
                            {usr.companyName && (
                              <div className="flex items-center gap-1.5 text-foreground/90 font-semibold text-primary">
                                <Briefcase className="h-3 w-3 shrink-0" />
                                <span>{usr.companyName}</span>
                              </div>
                            )}
                            {usr.address && (
                              <div className="text-foreground/50">
                                {usr.address}
                              </div>
                            )}
                            {!usr.phoneNumber && !usr.companyName && !usr.address && (
                              <span className="text-foreground/40 italic">Profil belum dilengkapi</span>
                            )}
                          </div>

                          <div className="text-[10px] text-foreground/50 text-right mt-1">
                            Bergabung: {usr.createdAt?.toDate ? usr.createdAt.toDate().toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' }) : '-'}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={deleteDialog.isOpen} onOpenChange={(isOpen) => setDeleteDialog(prev => ({ ...prev, isOpen }))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Konfirmasi Hapus</DialogTitle>
            <DialogDescription>
              Apakah Anda yakin ingin menghapus {deleteDialog.type === 'order' ? 'pesanan' : 'produk'} ini? Tindakan ini tidak dapat dibatalkan.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-end flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteDialog({ isOpen: false, type: 'order', id: '' })}
            >
              Batal
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={deleteDialog.type === 'order' ? confirmDeleteOrder : confirmDeleteProduct}
            >
              Hapus
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Write/Edit Article Dialog */}
      <Dialog open={isAddArticleOpen} onOpenChange={(open) => {
        setIsAddArticleOpen(open);
        if (!open) {
          setEditingArticle(null);
          setNewArticle({
            title: '',
            slug: '',
            content: '',
            excerpt: '',
            imageUrl: `https://picsum.photos/seed/${Math.random().toString().slice(2,8)}/800/400`,
            category: 'Konstruksi',
            status: 'published',
            relatedProductId: ''
          });
        }
      }}>
        <DialogContent className="w-[95vw] sm:max-w-[450px] md:max-w-[800px] border-none shadow-2xl rounded-[1.5rem] md:rounded-[2.5rem] overflow-hidden p-0 bg-card max-h-[90vh] flex flex-col">
          <div className="bg-primary p-4 md:p-8 shrink-0">
            <DialogHeader>
              <DialogTitle className="text-xl md:text-3xl font-black text-primary-foreground leading-tight">{editingArticle ? 'Edit Artikel' : 'Tulis Artikel Baru'}</DialogTitle>
              <DialogDescription className="text-primary-foreground/60 text-[10px] md:text-sm mt-1 md:mt-2">
                Gunakan editor ini untuk membuat artikel yang informatif bagi pelanggan Anda.
              </DialogDescription>
            </DialogHeader>
          </div>
          <form onSubmit={handleAddArticle} className="flex flex-col flex-1 overflow-hidden bg-card">
            <div className="flex-1 overflow-y-auto px-4 md:px-8 py-4 md:py-6 space-y-5 md:space-y-6">
                <div className="space-y-2">
                  <Label className="text-foreground text-[10px] md:text-sm font-bold uppercase tracking-widest opacity-60">Judul Artikel</Label>
                  <Input 
                    value={newArticle.title}
                    onChange={(e) => setNewArticle({...newArticle, title: e.target.value})}
                    placeholder="Contoh: Pentingnya Keselamatan Kerja..." 
                    className="border-foreground/10 focus:border-primary rounded-xl h-10 md:h-12 bg-muted/30"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div className="space-y-2">
                    <Label className="text-foreground text-[10px] md:text-sm font-bold uppercase tracking-widest opacity-60">Kategori</Label>
                    <Input 
                      value={newArticle.category}
                      onChange={(e) => setNewArticle({...newArticle, category: e.target.value})}
                      placeholder="Contoh: Berita, Tutorial" 
                      className="border-foreground/10 focus:border-primary rounded-xl h-10 md:h-12 bg-muted/30"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground text-[10px] md:text-sm font-bold uppercase tracking-widest opacity-60">Status</Label>
                    <select 
                      value={newArticle.status}
                      onChange={(e) => setNewArticle({...newArticle, status: e.target.value as any})}
                      className="flex h-10 md:h-12 w-full items-center justify-between rounded-xl border border-foreground/10 bg-muted/30 px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    >
                      <option value="published">Langsung Terbitkan</option>
                      <option value="draft">Simpan sebagai Draft</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-foreground text-[10px] md:text-sm font-bold uppercase tracking-widest opacity-60">Produk Terkait (Opsional)</Label>
                  <select 
                    value={newArticle.relatedProductId || ''}
                    onChange={(e) => setNewArticle({...newArticle, relatedProductId: e.target.value})}
                    className="flex h-10 md:h-12 w-full items-center justify-between rounded-xl border border-foreground/10 bg-muted/30 px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  >
                    <option value="">Tidak ada produk terkait</option>
                    {products.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  <p className="text-[10px] text-foreground/50 mt-1">Pilih produk yang relevan untuk diarahkan ke pembaca artikel ini.</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-foreground text-[10px] md:text-sm font-bold uppercase tracking-widest opacity-60">Ringkasan Singkat (Excerpt)</Label>
                  <textarea 
                    value={newArticle.excerpt}
                    onChange={(e) => setNewArticle({...newArticle, excerpt: e.target.value})}
                    className="w-full min-h-[80px] border border-foreground/10 rounded-xl p-3 text-sm focus:outline-none focus:border-primary bg-muted/30 text-foreground resize-none"
                    placeholder="Ringkasan singkat yang muncul di halaman depan..."
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-foreground text-[10px] md:text-sm font-bold uppercase tracking-widest opacity-60">Isi Artikel (Markdown Support)</Label>
                    <div className="flex gap-2">
                       <Button 
                         type="button" 
                         variant="outline" 
                         size="sm" 
                         className="h-7 text-[10px] rounded-lg border-gold/30 text-gold hover:bg-gold/10"
                         onClick={() => {
                           // Smart word clean up
                           let content = newArticle.content;
                           // Replace common Word characters
                           content = content.replace(/[\u201C\u201D]/g, '"'); // Smart double quotes
                           content = content.replace(/[\u2018\u2019]/g, "'"); // Smart single quotes
                           content = content.replace(/\u2013/g, "-");         // En dash
                           content = content.replace(/\u2014/g, "--");        // Em dash
                           content = content.replace(/\u2026/g, "...");       // Ellipsis
                           content = content.replace(/\u00A0/g, ' ');         // Non-breaking space
                           
                           // STAGE 1: Convert literal bullets to Markdown bullets
                           // Match common bullet characters at start of lines (with optional spaces)
                           content = content.replace(/^([ \t]*)([•●▪▫◦\-\+\*])([ \t]*)/gm, '* ');
                           
                           // STAGE 2: Clean up NUMBERED lists
                           // Detect patterns like "1. ", "1) ", "(1) " and ensure they are formatted as "1. "
                           content = content.replace(/^([ \t]*)([\(\[])?(\d+)([\.\)\]])([ \t]+)/gm, '$3. ');
                           
                           // STAGE 3: Clean up alphabet lists (a., b., etc) to consistent markdown
                           content = content.replace(/^([ \t]*)([\(\[])?([a-zA-Z])([\.\)\]])([ \t]+)/gm, '* ');

                           // STAGE 4: General clean up
                           content = content.replace(/[ ]{2,}/g, ' '); 
                           content = content.split('\n').map(line => line.trim()).join('\n');
                           
                           // STAGE 5: Ensure paragraphs have double line breaks
                           const singleBreaks = (content.match(/[^\n]\n[^\n]/g) || []).length;
                           const doubleBreaks = (content.match(/\n\n/g) || []).length;
                           if (singleBreaks > doubleBreaks * 0.5) { // More aggressive paragraph breaking
                             // But don't break lines that look like parts of a list
                             content = content.replace(/([^\n])\n([^\n\*1-9])/g, '$1\n\n$2');
                           }
                           
                           setNewArticle({...newArticle, content});
                           toast.success("Format teks dioptimasi!");
                         }}
                       >
                         Optimasi Teks dari Word
                       </Button>
                    </div>
                  </div>
                  
                  <Tabs defaultValue="edit" className="w-full">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                      <TabsList className="bg-muted p-1 rounded-xl w-fit">
                        <TabsTrigger value="edit" className="text-xs font-bold px-3">Editor Tulis</TabsTrigger>
                        <TabsTrigger value="preview" className="text-xs font-bold px-3">Pratinjau Langsung</TabsTrigger>
                        <TabsTrigger value="guide" className="text-xs font-bold px-3 text-gold">Panduan Kotak & Tabel ✨</TabsTrigger>
                      </TabsList>
                      <span className="text-[11px] text-foreground/50 hidden sm:inline">
                        Gunakan toolbar untuk menyisipkan kotak bagan atau tabel dengan 1-klik
                      </span>
                    </div>

                    <TabsContent value="edit" className="space-y-2 mt-0">
                      <ArticleEditorToolbar 
                        content={newArticle.content} 
                        onChange={(content) => setNewArticle({ ...newArticle, content })} 
                        textareaRef={articleTextareaRef} 
                      />
                      <textarea 
                        ref={articleTextareaRef}
                        value={newArticle.content}
                        onChange={(e) => setNewArticle({...newArticle, content: e.target.value})}
                        className="w-full min-h-[350px] md:min-h-[450px] border border-foreground/10 rounded-2xl p-4 text-sm focus:outline-none focus:border-primary bg-muted/30 text-foreground font-mono leading-relaxed"
                        placeholder="Tulis artikel Anda di sini... Gunakan tombol toolbar di atas untuk menyisipkan Tabel, Bagan Struktur, Alur Tahapan, atau Kotak Sorotan secara instan."
                      />
                    </TabsContent>

                    <TabsContent value="preview" className="mt-0">
                      <div className="w-full min-h-[350px] md:min-h-[450px] max-h-[550px] border border-border rounded-2xl p-6 md:p-8 bg-card overflow-y-auto shadow-inner">
                        <ArticleContentRenderer content={newArticle.content || "*Belum ada konten yang ditulis. Ketik di tab Editor untuk melihat pratinjau langsung di sini.*"} />
                      </div>
                    </TabsContent>

                    <TabsContent value="guide" className="mt-0">
                      <div className="w-full min-h-[350px] md:min-h-[450px] max-h-[550px] border border-border rounded-2xl p-4 md:p-6 bg-muted/20 overflow-y-auto space-y-6">
                        <div>
                          <h4 className="font-black text-foreground text-base mb-1">Panduan Membuat Kotak Struktur & Tabel</h4>
                          <p className="text-xs text-foreground/60">Klik tombol <strong>"Sisipkan ke Artikel"</strong> di bawah untuk langsung menggunakan format siap pakai berikut ke artikel Anda:</p>
                        </div>

                        {/* Template 1: Bagan Struktur Organisasi */}
                        <div className="p-4 rounded-2xl bg-card border border-gold/40 space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-black uppercase tracking-wider text-gold flex items-center gap-1.5">
                              🏢 1. Bagan Struktur Organisasi Proyek
                            </span>
                            <Button 
                              type="button" 
                              size="sm" 
                              className="h-7 text-xs bg-gold text-navy font-bold hover:bg-gold-light rounded-lg"
                              onClick={() => {
                                const template = `\`\`\`structure
title: Struktur Organisasi Pelaksana Proyek
[Direktur Utama / Kontraktor] | Penanggung Jawab Tertinggi
  -> [Project Manager (PM)] | Pimpinan Operasional Lapangan
    -> [Site Manager / Pelaksana Sipil] | Kontrol Fisik & Mutu
      - Mandor Pembesian
      - Mandor Bekisting
      - Mandor Cor Beton
    -> [Ahli K3 Konstruksi (SMKK)] | Petugas Keselamatan Kerja
      - Petugas Safety & HSE
      - Tim Tanggap Darurat & P3K
    -> [Quality Control & Surveyor] | Pengendalian Mutu & Ukur
\`\`\``;
                                setNewArticle(prev => ({
                                  ...prev,
                                  content: prev.content ? `${prev.content}\n\n${template}` : template
                                }));
                                toast.success("Template Bagan Struktur berhasil disisipkan!");
                              }}
                            >
                              Sisipkan ke Artikel
                            </Button>
                          </div>
                          <p className="text-[11px] text-foreground/60">
                            Membuat kotak hirarki organisasi bertingkat dengan garis cabang otomatis.
                          </p>
                        </div>

                        {/* Template 2: Tabel Teknis / AHSP */}
                        <div className="p-4 rounded-2xl bg-card border border-border space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-black uppercase tracking-wider text-primary flex items-center gap-1.5">
                              📊 2. Tabel Rincian Data / AHSP
                            </span>
                            <Button 
                              type="button" 
                              size="sm" 
                              className="h-7 text-xs bg-primary text-primary-foreground font-bold hover:bg-primary/90 rounded-lg"
                              onClick={() => {
                                const template = `| No | Posisi / Komponen | Uraian Tugas & Spesifikasi | Kualifikasi / Syarat |
|:--:|:------------------|:---------------------------|:---------------------|
| 1  | Project Manager   | Memimpin seluruh operasional proyek | SKK Jenjang 8 / S1 Sipil |
| 2  | Site Manager      | Pengawasan teknis lapangan & mutu  | SKK Jenjang 7 / S1 Sipil |
| 3  | Ahli K3 Konstruksi| Penerapan sistem keselamatan SMKK  | Sertifikat Ahli K3 |
| 4  | Quantity Surveyor | Monitoring volume & progres fisik  | S1 / D3 Teknik Sipil |`;
                                setNewArticle(prev => ({
                                  ...prev,
                                  content: prev.content ? `${prev.content}\n\n${template}` : template
                                }));
                                toast.success("Template Tabel berhasil disisipkan!");
                              }}
                            >
                              Sisipkan ke Artikel
                            </Button>
                          </div>
                          <p className="text-[11px] text-foreground/60">
                            Tabel data rapi dengan header kontras, zebra lines, dan responsif di layar ponsel.
                          </p>
                        </div>

                        {/* Template 3: Alur Tahapan Proyek */}
                        <div className="p-4 rounded-2xl bg-card border border-border space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-black uppercase tracking-wider text-blue-500 flex items-center gap-1.5">
                              🔄 3. Alur Tahapan / Workflow Proyek
                            </span>
                            <Button 
                              type="button" 
                              size="sm" 
                              className="h-7 text-xs bg-blue-600 text-white font-bold hover:bg-blue-700 rounded-lg"
                              onClick={() => {
                                const template = `\`\`\`alur
title: Alur Pengajuan Dokumen Tender & Penawaran
1. Persiapan Dokumen Kualifikasi | Mengumpulkan legalitas badan usaha (NIB, SBU, SKN, SPT Tahunan).
2. Perhitungan Analisa Harga Satuan (AHSP) | Menghitung harga bahan, upah, dan alat sesuai Permen PUPR.
3. Penyusunan Dokumen SMKK & RKK | Identifikasi bahaya kerja & mitigasi risiko keselamatan kerja.
4. Finalisasi Berkas Penawaran | Enkripsi file teknis dan harga sebelum batas waktu SPSE.
\`\`\``;
                                setNewArticle(prev => ({
                                  ...prev,
                                  content: prev.content ? `${prev.content}\n\n${template}` : template
                                }));
                                toast.success("Template Alur Tahapan berhasil disisipkan!");
                              }}
                            >
                              Sisipkan ke Artikel
                            </Button>
                          </div>
                          <p className="text-[11px] text-foreground/60">
                            Alur langkah bertahap dengan badge nomor urut dan garis penghubung vertikal elegan.
                          </p>
                        </div>

                        {/* Template 4: Kotak Sorotan / Callouts */}
                        <div className="p-4 rounded-2xl bg-card border border-border space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-black uppercase tracking-wider text-amber-500 flex items-center gap-1.5">
                              📦 4. Kotak Sorotan (Peringatan / Regulasi PUPR)
                            </span>
                            <Button 
                              type="button" 
                              size="sm" 
                              className="h-7 text-xs bg-amber-500 text-navy font-bold hover:bg-amber-600 rounded-lg"
                              onClick={() => {
                                const template = `> [!PERINGATAN]
> **Kepatuhan Regulasi PUPR:** Berdasarkan Peraturan Menteri PUPR No. 10 Tahun 2021, kelalaian dalam penyusunan dokumen SMKK dan RKK dapat mengakibatkan gugurnya penawaran tender dan sanksi administratif.`;
                                setNewArticle(prev => ({
                                  ...prev,
                                  content: prev.content ? `${prev.content}\n\n${template}` : template
                                }));
                                toast.success("Template Kotak Peringatan berhasil disisipkan!");
                              }}
                            >
                              Sisipkan ke Artikel
                            </Button>
                          </div>
                          <p className="text-[11px] text-foreground/60">
                            Kotak sorotan dengan ikon khusus (Info, Tips, Peringatan, atau Kotak Emas Penting).
                          </p>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>

                <div className="space-y-2">
                  <Label className="text-foreground text-[10px] md:text-sm font-bold uppercase tracking-widest opacity-60">URL Gambar Header</Label>
                  <Input 
                    value={newArticle.imageUrl}
                    onChange={(e) => setNewArticle({...newArticle, imageUrl: e.target.value})}
                    placeholder="https://..." 
                    className="border-foreground/10 focus:border-primary rounded-xl h-10 md:h-12 bg-muted/30"
                  />
                  {newArticle.imageUrl && (
                    <div className="mt-4 rounded-2xl border border-border overflow-hidden shadow-inner aspect-video">
                      <img src={newArticle.imageUrl} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
            </div>
            <div className="p-4 md:p-6 border-t border-border bg-muted/20 flex items-center justify-end gap-3 shrink-0">
              <Button type="button" variant="ghost" onClick={() => setIsAddArticleOpen(false)} className="rounded-xl font-bold py-5 px-4 md:px-6">Batal</Button>
              <Button type="submit" disabled={isSubmitting} className="bg-gold text-navy hover:bg-gold-light rounded-xl font-black uppercase tracking-widest text-[10px] md:text-xs py-5 px-6 md:px-8 shadow-xl shadow-gold/10">
                <FileText className="h-4 w-4 mr-2" /> {isSubmitting ? "Menyimpan..." : (editingArticle ? "Perbarui" : "Terbitkan Artikel")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Article Confirmation */}
      <Dialog open={deleteArticleDialog.isOpen} onOpenChange={(isOpen) => setDeleteArticleDialog(prev => ({ ...prev, isOpen }))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Hapus Artikel</DialogTitle>
            <DialogDescription>
              Apakah Anda yakin ingin menghapus artikel ini secara permanen?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-end flex-row gap-2">
            <Button variant="outline" onClick={() => setDeleteArticleDialog({ isOpen: false, id: '' })}>Batal</Button>
            <Button variant="destructive" onClick={confirmDeleteArticle} className="bg-red-600 hover:bg-red-700 text-white font-bold">Hapus Selamanya</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Regulation Dialog */}
      <Dialog open={isAddRegulationOpen} onOpenChange={(open) => {
        setIsAddRegulationOpen(open);
        if (!open) {
          setEditingRegulation(null);
          setNewRegulation({
            title: '',
            description: '',
            year: new Date().getFullYear(),
            category: 'Peraturan Pemerintah',
            fileUrl: ''
          });
        }
      }}>
        <DialogContent className="sm:max-w-[500px] border-none shadow-2xl rounded-[2rem] overflow-hidden p-0 bg-card">
          <div className="bg-navy p-6 pt-8">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-white">{editingRegulation ? 'Edit Regulasi' : 'Tambah Regulasi Baru'}</DialogTitle>
              <DialogDescription className="text-white/60">
                Masukkan detail peraturan atau dokumen regulasi baru.
              </DialogDescription>
            </DialogHeader>
          </div>
          <form onSubmit={handleAddRegulation} className="p-6 space-y-4">
            <div className="space-y-2">
              <Label className="font-bold">Judul Regulasi</Label>
              <Input 
                value={newRegulation.title}
                onChange={(e) => setNewRegulation({...newRegulation, title: e.target.value})}
                placeholder="Contoh: UU No. 2 Tahun 2017 tentang Jasa Konstruksi" 
                className="rounded-xl border-border bg-card"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-bold">Tahun</Label>
                <Input 
                  type="number"
                  value={newRegulation.year}
                  onChange={(e) => setNewRegulation({...newRegulation, year: Number(e.target.value)})}
                  className="rounded-xl border-border bg-card"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-bold">Kategori</Label>
                <select 
                  value={newRegulation.category}
                  onChange={(e) => setNewRegulation({...newRegulation, category: e.target.value})}
                  className="flex h-10 w-full items-center justify-between rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value="Peraturan Pemerintah">Peraturan Pemerintah</option>
                  <option value="Peraturan President">Peraturan Presiden</option>
                  <option value="Peraturan Menteri">Peraturan Menteri</option>
                  <option value="Keputusan Menteri">Keputusan Menteri</option>
                  <option value="Peraturan Lembaga">Peraturan Lembaga</option>
                  <option value="Keputusan Lembaga">Keputusan Lembaga</option>
                  <option value="Undang-Undang">Undang-Undang</option>
                  <option value="Surat Edaran">Surat Edaran</option>
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-bold">Deskripsi</Label>
              <textarea 
                value={newRegulation.description}
                onChange={(e) => setNewRegulation({...newRegulation, description: e.target.value})}
                className="w-full min-h-[80px] border border-border rounded-xl p-3 text-sm focus:outline-none focus:border-primary bg-card text-foreground"
                placeholder="Ringkasan tentang isi regulasi ini..."
              />
            </div>
            <div className="space-y-2">
              <Label className="font-bold">URL Download File</Label>
              <Input 
                value={newRegulation.fileUrl}
                onChange={(e) => setNewRegulation({...newRegulation, fileUrl: e.target.value})}
                placeholder="https://drive.google.com/..." 
                className="rounded-xl border-border bg-card"
              />
            </div>
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setIsAddRegulationOpen(false)} className="rounded-xl font-bold">Batal</Button>
              <Button type="submit" disabled={isSubmitting} className="bg-navy text-white hover:bg-navy/90 rounded-xl font-bold shadow-lg">
                {isSubmitting ? "Menyimpan..." : (editingRegulation ? "Perbarui" : "Simpan Regulasi")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Regulation Confirmation */}
      <Dialog open={deleteRegDialog.isOpen} onOpenChange={(isOpen) => setDeleteRegDialog(prev => ({ ...prev, isOpen }))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Hapus Regulasi</DialogTitle>
            <DialogDescription>
              Apakah Anda yakin ingin menghapus regulasi ini?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-end flex-row gap-2">
            <Button variant="outline" onClick={() => setDeleteRegDialog({ isOpen: false, id: '' })}>Batal</Button>
            <Button variant="destructive" onClick={confirmDeleteRegulation} className="bg-red-600 hover:bg-red-700 text-white font-bold">Hapus</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Interactive Official Receipt & Invoice Modal */}
      <ReceiptModal
        isOpen={isReceiptModalOpen}
        onClose={() => setIsReceiptModalOpen(false)}
        order={selectedReceiptOrder}
        userName={selectedReceiptOrder?.userName || 'Pelanggan Terdaftar'}
        userEmail={selectedReceiptOrder?.userEmail || ''}
      />
    </div>
  );
}
